import React, { useState, useEffect, useRef, memo } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ShoppingCart, Search, MapPin, Clock, Phone, MessageCircle, X, ChevronRight, ChevronLeft, Plus, Minus, CreditCard, Truck, CheckCircle, Package, Home, LogOut, User, Heart, Settings, CreditCard as CardIcon, MapPin as MapPinIcon, HelpCircle, Globe, Bell, FileText, Info, ChevronRight as Arrow, Star, Edit, Trash } from 'lucide-react';
import { auth, db } from './firebase';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth';
import { collection, addDoc, query, where, getDocs, orderBy, onSnapshot, doc, getDoc, serverTimestamp, updateDoc, deleteDoc, setDoc, increment } from 'firebase/firestore';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const stripePromise = loadStripe('pk_live_51T4rLGH9mj7mbq37xVVSZrqHYfmBsEfsVlarBjdSZ1FD1IHSzCTEwHytlr5GTzc3jyAdUHd2qV59nW31tPnXHsih00PTMtv6ow');

const Logo = () => (
  <div className="flex items-center gap-2">
    <img 
      src="/MARCHIO_FARMACIASUBITO.jpg"
      alt="FarmaciaSubito" 
      className="h-28 w-auto object-contain"
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// VERSIONE CORRENTE DEI TERMINI E CONDIZIONI
// Unico punto da modificare quando i ToS cambiano in modo sostanziale.
// Gli utenti registrati con una versione precedente vedranno il pop-up di
// ri-accettazione bloccante al primo accesso.
// ─────────────────────────────────────────────────────────────────────────────
const TERMS_VERSION = '2026-07-19';

// Splash screen di apertura: appare sopra l'app per ~3s poi svanisce.
// È puramente visivo — non tocca nessuna logica dell'app.
const SplashScreen = ({ onDone }) => {
  const [fade, setFade] = React.useState(false);

  React.useEffect(() => {
    const t1 = setTimeout(() => setFade(true), 2600);   // inizia dissolvenza
    const t2 = setTimeout(() => onDone && onDone(), 3200); // rimuove dal DOM
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: '#f4faf0',
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      opacity: fade ? 0 : 1,
      transition: 'opacity 0.6s ease',
      pointerEvents: fade ? 'none' : 'auto'
    }}>
      <style>{`
        @keyframes fsMarkIn { 0% { opacity:0; transform:scale(0.6);} 100% { opacity:1; transform:scale(1);} }
        @keyframes fsTitleIn { 0% { opacity:0; transform:translateY(12px);} 100% { opacity:1; transform:translateY(0);} }
        @keyframes fsTagIn { 0% { opacity:0;} 100% { opacity:1;} }
        @keyframes fsDot { 0%,100% { opacity:0.3; transform:translateY(0);} 50% { opacity:1; transform:translateY(-4px);} }
        .fs-mark { animation: fsMarkIn 0.9s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .fs-title { opacity:0; animation: fsTitleIn 0.7s ease forwards; animation-delay:0.9s; }
        .fs-tag { opacity:0; animation: fsTagIn 0.8s ease forwards; animation-delay:1.4s; }
        .fs-dots { opacity:0; animation: fsTagIn 0.5s ease forwards; animation-delay:1.7s; display:flex; gap:7px; }
        .fs-dot { width:7px; height:7px; border-radius:50%; background:#a9cf8e; animation: fsDot 1.2s infinite ease-in-out; }
        .fs-dot:nth-child(2){ animation-delay:0.2s; }
        .fs-dot:nth-child(3){ animation-delay:0.4s; }
      `}</style>

      <div className="fs-mark" style={{ width: 130, height: 130 }}>
        <svg width="130" height="130" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="56" fill="#e4f2d8" />
          <g>
            <path d="M60 20 C44 20 32 32 32 48 C32 68 60 92 60 92 C60 92 88 68 88 48 C88 32 76 20 60 20 Z" fill="#16a34a" />
            <rect x="54" y="36" width="12" height="28" rx="2" fill="#ffffff" />
            <rect x="46" y="44" width="28" height="12" rx="2" fill="#ffffff" />
          </g>
        </svg>
      </div>

      <div className="fs-title" style={{ marginTop: 22, fontSize: 26, fontWeight: 500, color: '#15803d', letterSpacing: '-0.3px' }}>
        FarmaciaSubito
      </div>
      <div className="fs-tag" style={{ marginTop: 6, fontSize: 13, color: '#4b7a3a' }}>
        La tua farmacia, a casa in poco tempo
      </div>

      <div className="fs-dots" style={{ position: 'absolute', bottom: 60 }}>
        <span className="fs-dot"></span><span className="fs-dot"></span><span className="fs-dot"></span>
      </div>
    </div>
  );
};

// Schermata mostrata quando l'admin sospende il servizio (nessun rider disponibile).
// Blocca i NUOVI ordini; gli ordini già in corso restano visibili e proseguono.
const ServizioSospeso = ({ onVediOrdini }) => (
  <div className="flex flex-col items-center justify-center text-center py-16 px-6">
    <div className="w-24 h-24 rounded-full bg-orange-50 flex items-center justify-center text-5xl mb-5">
      🕐
    </div>
    <h2 className="text-2xl font-black text-gray-800 mb-2">Torniamo presto!</h2>
    <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
      Il servizio è temporaneamente sospeso e non è possibile effettuare nuovi ordini.
      Ci scusiamo per il disagio, torneremo disponibili al più presto.
    </p>
    <div className="mt-6 bg-green-50 rounded-2xl p-4 max-w-xs">
      <p className="text-xs text-green-800">
        Hai già un ordine in corso? Puoi continuare a seguirlo normalmente.
      </p>
      {onVediOrdini && (
        <button
          onClick={onVediOrdini}
          className="mt-3 bg-green-600 text-white px-5 py-2 rounded-xl font-semibold text-sm active:scale-95 transition-transform"
        >
          Vedi i miei ordini
        </button>
      )}
    </div>
  </div>
);

const CheckoutForm = ({ total, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    // 1. Crea PaymentIntent sul server
    let clientSecret, paymentIntentId;
    try {
      const response = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/createPaymentIntent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: total,
          orderId: `order_${Date.now()}`,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.error) {
        setError(data.error || 'Errore durante il pagamento. Riprova.');
        setProcessing(false);
        return;
      }
      clientSecret = data.clientSecret;
      paymentIntentId = data.paymentIntentId;
    } catch (err) {
      setError('Errore di rete. Riprova.');
      setProcessing(false);
      return;
    }

    // 2. Conferma il pagamento con Stripe.js
    const cardElement = elements.getElement(CardElement);
    const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: cardElement },
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    // Pagamento riuscito
    setProcessing(false);
    onSuccess({
      paymentMethodId: paymentIntent.payment_method,
      paymentIntentId: paymentIntent.id,
      status: 'succeeded'
    });
  };

  const CARD_ELEMENT_OPTIONS = {
    style: {
      base: {
        color: '#32325d',
        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
        fontSmoothing: 'antialiased',
        fontSize: '16px',
        '::placeholder': { color: '#aab7c4' }
      },
      invalid: {
        color: '#fa755a',
        iconColor: '#fa755a'
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Carta di Credito/Debito
        </label>
        <div className="border-2 border-gray-300 rounded-lg p-4 bg-white">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
        
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="flex justify-between items-center text-lg font-bold">
          <span>Totale da pagare:</span>
          <span className="text-green-600">€{total.toFixed(2)}</span>
        </div>
      </div>

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
        >
          Annulla
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
        >
          {processing ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Elaborazione...
            </>
          ) : (
            <>
              <CreditCard size={20} />
              Paga €{total.toFixed(2)}
            </>
          )}
        </button>
      </div>
    </form>
  );
};

const RicettaCheckoutForm = ({ userEmail, userId, onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    setError(null);
    try {
      // 1. Crea/recupera il customer Stripe del cliente
      const custRes = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/createOrGetStripeCustomer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail, userId: userId })
      });
      const custData = await custRes.json();
      if (!custRes.ok || !custData.customerId) throw new Error(custData.error || 'Errore cliente Stripe');

      // 2. Crea il payment method dalla carta
      const cardElement = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
      });
      if (pmError) throw new Error(pmError.message);

      // 3. Allega la carta al customer (per l'addebito off_session all'invio)
      const attachRes = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/attachPaymentMethod', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: custData.customerId, paymentMethodId: paymentMethod.id })
      });
      const attachData = await attachRes.json();
      if (!attachRes.ok || !attachData.success) throw new Error(attachData.error || 'Errore registrazione carta');

      // La function può restituire un paymentMethodId ESISTENTE (se la carta era
      // già salvata) invece di quello appena creato: usiamo sempre quello che
      // torna dal server, così non si creano/usano duplicati.
      const pmIdDaUsare = attachData.paymentMethodId || paymentMethod.id;

      onSuccess({
        paymentMethodId: pmIdDaUsare,
        stripeCustomerId: custData.customerId,
        status: 'card_saved'
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="border-2 border-gray-200 rounded-xl p-3 bg-white focus-within:border-red-400">
        <CardElement options={{
          style: {
            base: { color: '#1f2937', fontSize: '15px', '::placeholder': { color: '#9ca3af' } },
            invalid: { color: '#ef4444' }
          }
        }} />
      </div>
      <p className="text-xs text-gray-500">La carta verrà addebitata (€4,99) al momento dell'invio della richiesta.</p>
      {error && <p className="text-red-600 text-sm">{error}</p>}
      <button type="submit" disabled={!stripe || processing}
        className="w-full bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2">
        {processing ? (
          <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>Registrazione carta...</>
        ) : (
          '💳 Registra carta'
        )}
      </button>
    </form>
  );
};

// Fix icone Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const LeafletMapCliente = memo(({ riderPosition, deliveryLat, deliveryLng }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const riderMarkerRef = useRef(null);
  const clienteMarkerRef = useRef(null);

  const riderIcon = L.divIcon({
    html: `<div style="width:32px;height:32px;border-radius:50%;background:#7c3aed;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>
    </div>`,
    iconSize: [32, 32], iconAnchor: [16, 16], className: ''
  });

  const clienteIcon = L.divIcon({
    html: `<div style="width:28px;height:28px;border-radius:50%;background:#16a34a;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center;">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="white"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
    </div>`,
    iconSize: [28, 28], iconAnchor: [14, 14], className: ''
  });

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;
    mapInstanceRef.current = L.map(mapRef.current, {
      center: [riderPosition.lat, riderPosition.lng],
      zoom: 15, zoomControl: true, attributionControl: false
    });
    L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', { maxZoom: 19 }).addTo(mapInstanceRef.current);
    riderMarkerRef.current = L.marker([riderPosition.lat, riderPosition.lng], { icon: riderIcon }).addTo(mapInstanceRef.current);
    if (deliveryLat && deliveryLng) {
      clienteMarkerRef.current = L.marker([deliveryLat, deliveryLng], { icon: clienteIcon }).addTo(mapInstanceRef.current);
      const bounds = L.latLngBounds([riderPosition.lat, riderPosition.lng], [deliveryLat, deliveryLng]);
      mapInstanceRef.current.fitBounds(bounds, { padding: [30, 30] });
    }
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current || !riderMarkerRef.current) return;
    riderMarkerRef.current.setLatLng([riderPosition.lat, riderPosition.lng]);
    if (!deliveryLat || !deliveryLng) {
      mapInstanceRef.current.setView([riderPosition.lat, riderPosition.lng], 15, { animate: true });
    }
  }, [riderPosition?.lat, riderPosition?.lng]);

  return <div ref={mapRef} style={{ width: '100%', height: '200px', borderRadius: '12px', marginTop: '8px', position: 'relative', zIndex: 0 }} />;
});

const FarmaciaSubito = () => {
  const [deliveryData, setDeliveryData] = useState({
    fullName: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    notes: '',
    codiceFiscale: '',
    customerLat: 0,
    customerLng: 0
  });
  const [showDeliveryForm, setShowDeliveryForm] = useState(false);
  // #6 — Mandato di rappresentanza: consenso attivo, NON preselezionato.
  // Richiesto a ogni ordine (si azzera dopo la creazione dell'ordine).
  const [mandatoAccettato, setMandatoAccettato] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [servizioAttivo, setServizioAttivo] = useState(true);
  // Stato notifiche push — serve per iOS, dove il permesso va chiesto da un
  // gesto esplicito dell'utente (un click), altrimenti Safari lo ignora.
  const [permessoNotifiche, setPermessoNotifiche] = useState('default');
  const [attivandoNotifiche, setAttivandoNotifiche] = useState(false);
  // Supplemento festivo (sabato/domenica/festivi) — configurabile dall'Admin
  const [supplFestivo, setSupplFestivo] = useState({ attivo: true, importo: 1.00 });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // login | register | reset
  const [resetSent, setResetSent] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  // Consenso marketing (GDPR): separato, facoltativo, non pre-spuntato.
  const [consensoMarketing, setConsensoMarketing] = useState(false);
  // Accettazione Termini e Privacy: OBBLIGATORIA per registrarsi.
  const [accettoTermini, setAccettoTermini] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [showLegalPopup, setShowLegalPopup] = useState(false);
  // Ri-accettazione ToS: mostrato agli utenti registrati con una versione
  // precedente a TERMS_VERSION. Bloccante finché non accettano.
  const [showRiaccettazioneToS, setShowRiaccettazioneToS] = useState(false);
  const [salvandoRiaccettazione, setSalvandoRiaccettazione] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Impostazioni zona copertura — caricate da Firestore
  const [zonaImpostazioni, setZonaImpostazioni] = useState({
    latitudine: 41.1297,
    longitudine: 14.7824,
    raggioKm: 5,
    capAccettati: ['82100']
  });
  const [currentOrder, setCurrentOrder] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    { sender: 'pharmacist', text: 'Buongiorno! Come posso aiutarla oggi?', time: '10:30' }
  ]);
  const [newMessage, setNewMessage] = useState('');
  const [showNotification, setShowNotification] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [ricettaSelezionata, setRicettaSelezionata] = useState(null);
  const [showRicettaRequest, setShowRicettaRequest] = useState(false);
  const [ricettaRequestData, setRicettaRequestData] = useState({ 
    note: '', ricettaUrl: null, ricettaNome: null,
    fullName: '', phone: '', address: '', city: '', zipCode: ''
  });
  const [uploadingRicettaRequest, setUploadingRicettaRequest] = useState(false);
  const [ricettaRequestSent, setRicettaRequestSent] = useState(false);
  const [showRicettaPayment, setShowRicettaPayment] = useState(false);
  const [ricettaPaymentDone, setRicettaPaymentDone] = useState(false);
  const [ricettaIsPreOrder, setRicettaIsPreOrder] = useState(false);
  const [ricettaSelectedSlot, setRicettaSelectedSlot] = useState(null);
  const [ricettaPaymentInfo, setRicettaPaymentInfo] = useState(null);
  const [ricette, setRicette] = useState([]);
  const [uploadingRicetta, setUploadingRicetta] = useState(false);

  // Carica impostazioni zona da Firestore all'avvio
  useEffect(() => {
    const loadZona = async () => {
      try {
        const snap = await getDoc(doc(db, 'impostazioni', 'zona'));
        if (snap.exists()) {
          setZonaImpostazioni(snap.data());
        }
      } catch (e) {
        console.warn('Usa zona default:', e);
      }
    };
    loadZona();
  }, []);

  // Carica margine prudenziale da Firestore (impostazioni/margine)
  useEffect(() => {
    const loadMargine = async () => {
      try {
        const snap = await getDoc(doc(db, 'impostazioni', 'margine'));
        if (snap.exists() && typeof snap.data().percentuale === 'number') {
          setMargine(snap.data().percentuale);
        }
      } catch (e) {
        console.warn('Usa margine default 10%:', e);
      }
    };
    loadMargine();
  }, []);

  // Legge il permesso notifiche DOPO il montaggio del componente.
  // NON va letto durante il render (nell'useState iniziale): su iOS faceva
  // crashare l'app all'avvio. Dentro useEffect + try/catch è sicuro ovunque.
  useEffect(() => {
    try {
      if (window.Notification && window.Notification.permission) {
        setPermessoNotifiche(window.Notification.permission);
      }
    } catch (e) {
      // Alcuni webview non espongono Notification: resta 'default'
    }
  }, []);

  // Carica il consenso marketing dell'utente loggato (per l'interruttore nel Profilo)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const caricaConsenso = async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        if (snap.exists() && typeof snap.data().consensoMarketing === 'boolean') {
          setConsensoMarketing(snap.data().consensoMarketing);
        }
      } catch (e) {
        console.warn('Consenso non caricato:', e.message);
      }
    };
    caricaConsenso();
  }, [currentUser?.uid]);

  // ── RI-ACCETTAZIONE TERMINI ──
  // Se l'utente ha accettato una versione precedente dei ToS (o nessuna),
  // mostra il pop-up bloccante. Chi non ha mai accettato nulla (utenti
  // registrati prima dell'introduzione del checkbox) rientra nello stesso caso.
  //
  // NOTA: si usa onSnapshot e NON getDoc. getDoc può restituire il documento
  // dalla cache offline di Firestore, che dopo l'accettazione risulta ancora
  // alla versione precedente: il pop-up ricomparirebbe a ogni riavvio anche
  // con il dato corretto già salvato sul server. onSnapshot resta invece
  // allineato al server e si aggiorna da solo dopo la scrittura.
  useEffect(() => {
    if (!currentUser?.uid) {
      setShowRiaccettazioneToS(false);
      return;
    }
    const unsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (snap) => {
        if (!snap.exists()) return;
        const versioneAccettata = snap.data().termsVersion || null;
        setShowRiaccettazioneToS(versioneAccettata !== TERMS_VERSION);
      },
      (e) => {
        // In caso di errore NON blocchiamo l'utente: meglio non mostrare il
        // pop-up che rendere l'app inutilizzabile.
        console.warn('Verifica versione ToS non riuscita:', e.message);
        setShowRiaccettazioneToS(false);
      }
    );
    return () => unsub();
  }, [currentUser?.uid]);

  // Carica il supplemento festivo (impostazioni/supplementoFestivo)
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'impostazioni', 'supplementoFestivo'),
      (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          setSupplFestivo({
            attivo: typeof d.attivo === 'boolean' ? d.attivo : true,
            importo: typeof d.importo === 'number' ? d.importo : 1.00
          });
        }
      },
      () => {} // in caso di errore resta il default
    );
    return () => unsub();
  }, []);

  // Stato del servizio (impostazioni/servizio): se sospeso, blocca i NUOVI ordini.
  // Ascolto in tempo reale: se l'admin sospende/riattiva, l'app si aggiorna subito.
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'impostazioni', 'servizio'),
      (snap) => {
        if (snap.exists() && typeof snap.data().attivo === 'boolean') {
          setServizioAttivo(snap.data().attivo);
        } else {
          setServizioAttivo(true); // default: servizio attivo
        }
      },
      () => setServizioAttivo(true) // in caso di errore non blocchiamo il servizio
    );
    return () => unsub();
  }, []);

  // Carica ricette utente
  useEffect(() => {
    if (!currentUser?.uid) return;
    const q = query(collection(db, 'users', currentUser.uid, 'ricette'), orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(q, snap => {
      setRicette(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [completedOrders, setCompletedOrders] = useState(0);
  const [orderListener, setOrderListener] = useState(null);
  const [mancia, setMancia] = useState(0);
  const [manciaPersonalizzata, setManciaPersonalizzata] = useState('');
  const [codicePromo, setCodicePromo] = useState('');
  const [promoApplicata, setPromoApplicata] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [products, setProducts] = useState([]);
  const [margine, setMargine] = useState(10); // percentuale margine prudenziale, default 10%
  const [showSuggestBanner, setShowSuggestBanner] = React.useState(false);
  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [showProblemaModal, setShowProblemaModal] = React.useState(false);
  const [reviewedOrderId, setReviewedOrderId] = React.useState(null);

  // ORARI DI SERVIZIO
  const ORARI = [
    { start: 9, end: 13 },   // Mattina
    { start: 16, end: 20.5 } // Pomeriggio (20:30)
  ];

  const isServiceOpen = () => {
    const now = new Date();
    const h = now.getHours() + now.getMinutes() / 60;
    return ORARI.some(o => h >= o.start && h < o.end);
  };

  // Genera tutti gli slot da 30 minuti per le fasce disponibili
  const getSlotDisponibili = () => {
    const now = new Date();
    const h = now.getHours() + now.getMinutes() / 60;
    const slots = [];

    const aggiungiSlot = (oraInizio, oraFine, giorno, labelGiorno) => {
      let current = oraInizio;
      while (current + 0.5 <= oraFine) {
        const next = current + 0.5;
        const hStart = Math.floor(current);
        const mStart = Math.round((current % 1) * 60);
        const hEnd = Math.floor(next);
        const mEnd = Math.round((next % 1) * 60);
        const label = `${labelGiorno} ${String(hStart).padStart(2,'0')}:${String(mStart).padStart(2,'0')} - ${String(hEnd).padStart(2,'0')}:${String(mEnd).padStart(2,'0')}`;
        const date = new Date(giorno);
        date.setHours(hStart, mStart, 0, 0);
        slots.push({ date, label, oraInizio: current });
        current = next;
      }
    };

    // Slot di oggi non ancora passati (con 5 minuti di margine)
    const hConMargine = h + 5/60;
    for (const orario of ORARI) {
      if (hConMargine < orario.end) {
        const startEffettivo = Math.max(orario.start, Math.ceil(hConMargine * 2) / 2);
        aggiungiSlot(startEffettivo, orario.end, new Date(), 'Oggi');
      }
    }

    // Se non ci sono slot oggi, aggiungi domani
    if (slots.length === 0) {
      const domani = new Date();
      domani.setDate(domani.getDate() + 1);
      for (const orario of ORARI) {
        aggiungiSlot(orario.start, orario.end, domani, 'Domani');
      }
    }

    return slots;
  };

  // Compatibilità con codice esistente
  const getNextSlot = () => {
    const slots = getSlotDisponibili();
    return slots.length > 0 ? slots[0] : null;
  };

  const [isPreOrder, setIsPreOrder] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSlotPicker, setShowSlotPicker] = useState(false);

  // Mostra banner suggerimento dopo 2 secondi se non già visto oggi
  React.useEffect(() => {
    const lastSeen = localStorage.getItem('suggestBannerSeen');
    const today = new Date().toDateString();
    if (lastSeen !== today) {
      setTimeout(() => setShowSuggestBanner(true), 2000);
    }
  }, []);

  // Inizializza FCM e salva token.
  // Su Android nativo (Capacitor) usa @capacitor/push-notifications (notifiche
  // native, funzionano anche a schermo bloccato / app chiusa).
  // Nel browser usa il web FCM come prima.
  const initFCM = async (uid) => {
    console.log('[FCM] initFCM chiamata per uid:', uid);
    // ── PERCORSO NATIVO (Android/iOS via Capacitor) ──
    try {
      const { Capacitor } = await import('@capacitor/core');
      const isNative = Capacitor.isNativePlatform && Capacitor.isNativePlatform();
      console.log('[FCM] piattaforma nativa?', isNative);
      if (isNative) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        console.log('[FCM] plugin push caricato');

        // Chiede il permesso notifiche
        let perm = await PushNotifications.checkPermissions();
        console.log('[FCM] permesso attuale:', perm.receive);
        if (perm.receive !== 'granted') {
          perm = await PushNotifications.requestPermissions();
          console.log('[FCM] permesso dopo richiesta:', perm.receive);
        }
        if (perm.receive !== 'granted') {
          console.warn('[FCM] Permesso notifiche NEGATO');
          return;
        }

        // Registra il dispositivo su FCM
        await PushNotifications.register();

        // Token ricevuto → lo salviamo su Firestore (users/{uid}.fcmToken)
        PushNotifications.addListener('registration', async (token) => {
          try {
            await setDoc(
              doc(db, 'users', uid),
              { fcmToken: token.value, fcmUpdatedAt: new Date().toISOString(), fcmPlatform: 'android' },
              { merge: true }
            );
            console.log('FCM token nativo salvato');
          } catch (e) {
            console.warn('Errore salvataggio token:', e.message);
          }
        });

        PushNotifications.addListener('registrationError', (err) => {
          console.warn('Errore registrazione push:', JSON.stringify(err));
        });

        // Notifica ricevuta con app in primo piano
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push ricevuta:', notification.title);
        });

        // L'utente ha toccato la notifica
        PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = action.notification?.data || {};
          // Se la notifica riguarda un ordine, apri la sezione ordini/tracking
          if (data.orderId || data.type === 'order') {
            setCurrentView('orders');
          }
        });

        return; // fine percorso nativo
      }
    } catch (e) {
      console.warn('Push nativo non disponibile, uso il web FCM:', e.message);
    }

    // ── PERCORSO WEB (browser / PWA) ──
    // Allineato all'App Rider, che funziona su iOS:
    //  - window.Notification (non il globale Notification)
    //  - getToken SENZA serviceWorkerRegistration (se getRegistration() torna
    //    undefined, getToken fallisce: meglio lasciare che Firebase lo trovi)
    try {
      if (!window.Notification) {
        console.log('[FCM] Notifiche non supportate su questo dispositivo');
        return;
      }

      let permission = window.Notification.permission;
      if (permission === 'default') {
        permission = await window.Notification.requestPermission();
      }
      if (permission !== 'granted') {
        console.log('[FCM] Permesso non concesso:', permission);
        return;
      }

      const messaging = getMessaging();
      const token = await getToken(messaging, {
        vapidKey: 'BLvztUjmKRJfT0sEIDNtbZMRRHEsJfvXXLH8khBa8IUQBwlteItHS2xBhCO9GrWJxbNx7l6y-EGoJv_Qx59gqcc'
      });

      if (token) {
        await setDoc(doc(db, 'users', uid), { fcmToken: token, fcmUpdatedAt: new Date().toISOString() }, { merge: true });
        console.log('[FCM] Token web salvato');
      }

      // Notifiche in foreground
      onMessage(messaging, (payload) => {
        const { title, body } = payload.notification || {};
        if (window.Notification.permission === 'granted') {
          new window.Notification(title || 'FarmaciaSubito', { body: body || '', icon: '/logo192.png' });
        }
      });
    } catch (e) {
      console.warn('[FCM] non disponibile:', e.message);
    }
  };

  // Attivazione notifiche da un CLICK esplicito (necessario su iOS).
  // Replica ESATTAMENTE il pattern dell'App Rider, che funziona su iOS:
  //  - window.Notification (non il globale Notification)
  //  - getToken SENZA serviceWorkerRegistration (Firebase lo trova da solo)
  const attivaNotifiche = async () => {
    if (!currentUser?.uid) return;
    setAttivandoNotifiche(true);
    try {
      if (!window.Notification) {
        alert('Le notifiche non sono supportate su questo dispositivo.');
        setAttivandoNotifiche(false);
        return;
      }

      let permission = window.Notification.permission;
      if (permission === 'default') {
        permission = await window.Notification.requestPermission();
      }
      setPermessoNotifiche(permission);

      if (permission !== 'granted') {
        if (permission === 'denied') {
          alert('Hai negato le notifiche. Per riattivarle, vai nelle impostazioni del tuo dispositivo.');
        }
        setAttivandoNotifiche(false);
        return;
      }

      const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
      const messaging = getMessaging();

      const token = await getToken(messaging, {
        vapidKey: 'BLvztUjmKRJfT0sEIDNtbZMRRHEsJfvXXLH8khBa8IUQBwlteItHS2xBhCO9GrWJxbNx7l6y-EGoJv_Qx59gqcc'
      });

      if (token) {
        await setDoc(doc(db, 'users', currentUser.uid), {
          fcmToken: token,
          fcmUpdatedAt: new Date().toISOString()
        }, { merge: true });
        alert('✅ Notifiche attivate!');
      } else {
        alert('Non è stato possibile registrare il dispositivo. Riprova.');
      }

      onMessage(messaging, (payload) => {
        if (window.Notification.permission === 'granted') {
          new window.Notification(payload.notification?.title || 'FarmaciaSubito', {
            body: payload.notification?.body || '',
            icon: '/logo192.png'
          });
        }
      });

    } catch (e) {
      console.error('Errore attivazione notifiche:', e);
      alert('Errore: ' + (e.message || 'riprova'));
    }
    setAttivandoNotifiche(false);
  };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.log('[FCM] onAuthStateChanged — user:', !!user, 'emailVerified:', user?.emailVerified);
      if (user && user.emailVerified) {
        setCurrentUser({ email: user.email, uid: user.uid });
        setIsLoggedIn(true);
        initFCM(user.uid);
      } else {
        setCurrentUser(null);
        setIsLoggedIn(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('Inserisci email e password');
      return;
    }

    if (authMode === 'register' && loginPassword !== confirmPassword) {
      setLoginError('Le password non coincidono');
      return;
    }

    // I Termini e la Privacy devono essere accettati per registrarsi
    if (authMode === 'register' && !accettoTermini) {
      setLoginError('⚠️ Per registrarti devi accettare i Termini e Condizioni e la Privacy Policy.');
      return;
    }

    if (loginPassword.length < 6) {
      setLoginError('La password deve essere di almeno 6 caratteri');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      if (authMode === 'login') {
        const userCredential = await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
        if (!userCredential.user.emailVerified) {
          await signOut(auth);
          setLoginError('📧 Email non verificata. Controlla la tua casella (e lo SPAM!) e clicca il link di conferma da noreply@farmacia-subito.firebaseapp.com');
          return;
        }
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, loginEmail, loginPassword);
        await sendEmailVerification(userCredential.user);

        // Salva accettazione Termini/Privacy (tracciata: data + versione) e
        // consenso marketing (GDPR: serve sapere SE e QUANDO è stato dato)
        try {
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: loginEmail,
            // Accettazione obbligatoria (senza, la registrazione è bloccata sopra)
            termsAccepted: true,
            termsAcceptedAt: new Date().toISOString(),
            termsVersion: TERMS_VERSION,
            // Consenso marketing: facoltativo
            consensoMarketing: consensoMarketing,
            consensoMarketingData: consensoMarketing ? new Date().toISOString() : null,
            createdAt: new Date().toISOString()
          }, { merge: true });
        } catch (e) {
          console.warn('Errore salvataggio consensi:', e.message);
        }

        // Invia email di benvenuto
        try {
          await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: loginEmail,
              subject: '🎉 Benvenuto su FarmaciaSubito!',
              html: `
                <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
                  <div style="background: #2563eb; padding: 32px; text-align: center; border-radius: 12px 12px 0 0;">
                    <h1 style="color: white; margin: 0; font-size: 26px;">💊 FarmaciaSubito</h1>
                    <p style="color: #bfdbfe; margin: 8px 0 0 0;">Farmaci a domicilio in pochi minuti</p>
                  </div>
                  <div style="background: #f8fafc; padding: 32px; border-radius: 0 0 12px 12px; border: 1px solid #e2e8f0;">
                    <h2 style="color: #1e40af; margin-top: 0;">Benvenuto! 👋</h2>
                    <p style="font-size: 15px; line-height: 1.6;">
                      Grazie per esserti registrato su <strong>FarmaciaSubito</strong> — il servizio che ti porta i farmaci
                      direttamente a casa tua in pochi minuti, dalla farmacia più vicina a te.
                    </p>
                    <div style="background: white; border-left: 4px solid #2563eb; padding: 20px; margin: 24px 0; border-radius: 0 8px 8px 0;">
                      <h3 style="margin: 0 0 12px 0; color: #2563eb;">🛵 Come funziona</h3>
                      <ol style="margin: 0; padding-left: 20px; line-height: 2; color: #374151;">
                        <li>Cerca il farmaco che ti serve nell'app</li>
                        <li>Inserisci il tuo indirizzo di consegna</li>
                        <li>Un rider acquista il farmaco nella farmacia più vicina a te</li>
                        <li>Ricevi il farmaco direttamente a casa in pochi minuti</li>
                      </ol>
                    </div>
                    <div style="background: #eff6ff; border: 1px solid #bfdbfe; padding: 16px; border-radius: 8px; margin: 20px 0;">
                      <p style="margin: 0; font-size: 14px; color: #1e40af;">
                        📧 <strong>Prossimo passo:</strong> verifica la tua email cliccando il link che ti abbiamo appena inviato, poi accedi all'app e fai il tuo primo ordine!
                      </p>
                    </div>
                    <div style="text-align: center; margin: 28px 0;">
                      <a href="https://app.farmaciasubito.it" style="background: #2563eb; color: white; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 16px; display: inline-block;">
                        🚀 Vai all'app
                      </a>
                    </div>
                    <p style="font-size: 14px; color: #64748b; line-height: 1.6;">
                      Per qualsiasi domanda scrivici a
                      <a href="mailto:info@farmaciasubito.it" style="color: #2563eb;">info@farmaciasubito.it</a>
                    </p>
                    <p style="font-size: 15px; margin-top: 24px;">A presto! 💊<br><strong>Il team FarmaciaSubito</strong></p>
                  </div>
                  <p style="text-align: center; font-size: 12px; color: #94a3b8; margin-top: 16px;">
                    FarmaciaSubito — Benevento |
                    <a href="https://www.farmaciasubito.it/privacy-policy.html" style="color: #94a3b8;">Privacy Policy</a>
                  </p>
                </div>
              `
            })
          });
        } catch (emailError) {
          console.warn('Email benvenuto non inviata:', emailError);
        }

        await signOut(auth);
        setAuthMode('login');
        setLoginError('');
        setLoginEmail('');
        setLoginPassword('');
        setConfirmPassword('');
        alert('✅ Account creato!\n\nTi abbiamo inviato una email di verifica da noreply@farmacia-subito.firebaseapp.com\n\n📧 Controlla la tua casella di posta.\n📁 Se non la trovi, controlla anche la cartella SPAM.\n\nClicca il link nell\'email per verificare il tuo account.');
        return;
      }
      setIsLoggedIn(true);
      // Inizializza le notifiche push anche su questo percorso di login
      // (onAuthStateChanged potrebbe non richiamarlo se già montato)
      if (auth.currentUser?.uid) {
        initFCM(auth.currentUser.uid);
      }
    } catch (error) {
      if (error.code === 'auth/user-not-found' || error.code === 'auth/invalid-credential') {
        setLoginError('Email o password errati');
      } else if (error.code === 'auth/email-already-in-use') {
        setLoginError('Email già registrata — accedi invece di registrarti');
      } else if (error.code === 'auth/wrong-password') {
        setLoginError('Password errata');
      } else if (error.code === 'auth/invalid-email') {
        setLoginError('Email non valida');
      } else if (error.code === 'auth/too-many-requests') {
        setLoginError('Troppi tentativi. Riprova tra qualche minuto.');
      } else {
        console.error('Errore login:', error.code, error.message);
        setLoginError(`Errore: ${error.code} — ${error.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!loginEmail) {
      setLoginError('Inserisci la tua email');
      return;
    }
    setIsLoading(true);
    setLoginError('');
    try {
      await sendPasswordResetEmail(auth, loginEmail);
      setResetSent(true);
    } catch (error) {
      setLoginError('Email non trovata o non valida');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
    setIsLoggedIn(false);
    setCurrentUser(null);
    setCart([]);
    setRicettaSelezionata(null);
    setCurrentView('home');
    if (orderListener) {
      orderListener();
      setOrderListener(null);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      setCart(cart.map(item => 
        item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 2000);
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, change) => {
    setCart(cart.map(item => {
      if (item.id === productId) {
        const newQuantity = item.quantity + change;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  };

  const getServiceFee = () => {
    const subtotal = getCartTotal();
    const fee = subtotal * 0.08;
    return Math.min(Math.max(fee, 0.79), 3.99);
  };

  const getSmallOrderSupplement = () => {
    const subtotal = getCartTotal();
    if (subtotal >= 10) return 0;
    return Math.min(2.00, 10 - subtotal);
  };

  // Festività italiane (fisse). Capodanno, Epifania, Liberazione, Festa del Lavoro,
  // Festa della Repubblica, Ferragosto, Ognissanti, Immacolata, Natale, S. Stefano.
  // (Pasqua e Pasquetta sono mobili: gestite a parte.)
  const FESTIVI_FISSI = ['01-01','01-06','04-25','05-01','06-02','08-15','11-01','12-08','12-25','12-26'];

  const isGiornoFestivo = (data) => {
    const giorno = data.getDay(); // 0 = domenica, 6 = sabato
    if (giorno === 0 || giorno === 6) return true;
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    const dd = String(data.getDate()).padStart(2, '0');
    return FESTIVI_FISSI.includes(`${mm}-${dd}`);
  };

  // Supplemento festivo: si applica se la CONSEGNA avviene di sabato, domenica o
  // in un giorno festivo. Per i preordini vale la data dello slot scelto.
  const getSupplementoFestivo = () => {
    if (!supplFestivo.attivo) return 0;
    const slot = isPreOrder ? (selectedSlot || getNextSlot()) : null;
    const dataConsegna = slot?.date || new Date();
    return isGiornoFestivo(dataConsegna) ? (supplFestivo.importo || 0) : 0;
  };

  const getScontoPromo = () => {
    if (!promoApplicata) return 0;
    if (promoApplicata.tipo === 'percentuale') return getCartTotal() * (promoApplicata.valore / 100);
    if (promoApplicata.tipo === 'fisso') return Math.min(promoApplicata.valore, getCartTotal());
    if (promoApplicata.tipo === 'spedizione_gratis') return 3.99;
    return 0;
  };

  const getFinalTotal = () => {
    return Math.max(0, getCartTotal() + 
           getServiceFee() + 
           getSmallOrderSupplement() + 
           getSupplementoFestivo() +
           3.99 + 
           mancia -
           getScontoPromo());
  };

  const applicaCodicePromo = async () => {
    if (!codicePromo.trim()) return;
    setPromoLoading(true);
    try {
      const { getDocs, collection, query, where } = await import('firebase/firestore');
      const snap = await getDocs(query(
        collection(db, 'promo-codici'),
        where('codice', '==', codicePromo.toUpperCase().trim()),
        where('attivo', '==', true)
      ));
      if (snap.empty) {
        alert('❌ Codice non valido o scaduto');
        setPromoLoading(false);
        return;
      }
      const promo = { id: snap.docs[0].id, ...snap.docs[0].data() };
      // Controlla scadenza
      if (promo.scadenza && new Date(promo.scadenza) < new Date()) {
        alert('❌ Codice scaduto');
        setPromoLoading(false);
        return;
      }
      // Controlla max usi
      if (promo.maxUsi && promo.usiTotali >= promo.maxUsi) {
        alert('❌ Codice esaurito');
        setPromoLoading(false);
        return;
      }
      setPromoApplicata(promo);
      alert(`✅ Codice applicato! Sconto: ${promo.tipo === 'percentuale' ? `-${promo.valore}%` : promo.tipo === 'fisso' ? `-€${promo.valore}` : 'Spedizione gratis'}`);
    } catch (e) {
      alert('❌ Errore: ' + e.message);
    } finally {
      setPromoLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentInfo) => {
    // Sicurezza: se il servizio è stato sospeso nel frattempo, non creare l'ordine.
    if (!servizioAttivo) {
      alert('🕐 Il servizio è temporaneamente sospeso. Non è possibile completare l\'ordine in questo momento.');
      return;
    }
    if (!currentUser?.uid) {
      alert('Errore: utente non autenticato!');
      return;
      }
      
    const total = getFinalTotal();
    
    try {
      const orderData = {
        userId: currentUser?.uid,
        userEmail: currentUser?.email || deliveryData.phone,
        customerName: deliveryData.fullName,
        customerPhone: deliveryData.phone,
        deliveryAddress: deliveryData.address,
        deliveryCity: deliveryData.city,
        deliveryZipCode: deliveryData.zipCode,
        deliveryNotes: deliveryData.notes,
        codiceFiscale: deliveryData.codiceFiscale || '',
        deliveryLat: deliveryData.customerLat || 0,
        deliveryLng: deliveryData.customerLng || 0,
        farmaciaId: 'nearest',
        pharmacyName: 'Farmacia più vicina',
        pharmacyAddress: '',
        pharmacyLat: 0,
        pharmacyLng: 0,
        items: cart.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          description: item.description,
          imageUrl: item.imageUrl || ''
        })),
        subtotal: getCartTotal(),
        serviceFee: getServiceFee(),
        smallOrderSupplement: getSmallOrderSupplement(),
        supplementoFestivo: getSupplementoFestivo(),
        deliveryFee: 3.99,
        mancia: mancia,
        promoCode: promoApplicata?.codice || null,
        promoSconto: getScontoPromo(),
        total: total,
        status: isPreOrder ? 'scheduled' : 'preparing',
        isPreOrder: isPreOrder,
        scheduledFor: isPreOrder ? (selectedSlot || getNextSlot())?.date.toISOString() : null,
        scheduledLabel: isPreOrder ? (selectedSlot || getNextSlot())?.label : null,
        paymentStatus: paymentInfo.status === 'succeeded' ? 'paid' : 'pending',
        // P3 FIX: paymentMethod deve essere 'card' o 'cash' (NON un pm_).
        // Il pm_ va in paymentMethodId; il pi_ (necessario per il rimborso) in paymentIntentId.
        paymentMethod: paymentInfo.paymentMethodId === 'cash' ? 'cash' : 'card',
        paymentMethodId: (paymentInfo.paymentMethodId && paymentInfo.paymentMethodId !== 'cash')
          ? paymentInfo.paymentMethodId
          : null,
        paymentIntentId: paymentInfo.paymentIntentId || null,
        ricettaUrl: cart.some(i => i.prescription) ? (ricettaSelezionata?.url || null) : null,
        ricettaNome: cart.some(i => i.prescription) ? (ricettaSelezionata?.nome || null) : null,
        // #6 — Prova del mandato conferito per questo specifico ordine
        mandatoRappresentanza: true,
        mandatoAccettatoAt: new Date().toISOString(),
        mandatoVersione: '2026-07-19',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(db, 'orders'), orderData);

      // Chiama Cloud Function per assegnare rider
      if (!isPreOrder) {
        fetch('https://us-central1-farmacia-subito.cloudfunctions.net/assegnaRider', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ orderId: docRef.id })
        }).catch(e => console.warn('assegnaRider error:', e));
      }

      // Aggiorna subito a 'ready' per triggerare la Cloud Function di assegnazione rider
      if (!isPreOrder) {
        await updateDoc(doc(db, 'orders', docRef.id), {
          status: 'ready'
        });
      }

      // Incrementa contatore utilizzi codice promo (best-effort: non deve
      // MAI bloccare la creazione dell'ordine. Il pagamento è già andato a
      // buon fine e l'ordine è creato: se questa scrittura fallisce — es. per
      // permessi sulla collection promo-codici — la ignoriamo silenziosamente.)
      if (promoApplicata?.id) {
        try {
          await updateDoc(doc(db, 'promo-codici', promoApplicata.id), {
            usiTotali: increment(1)
          });
        } catch (promoErr) {
          console.warn('Impossibile aggiornare il contatore promo (non bloccante):', promoErr);
        }
        setPromoApplicata(null);
        setCodicePromo('');
      }
      
      setCurrentOrder({
        ...orderData,
        id: docRef.id
      });
      
      const unsubscribe = onSnapshot(doc(db, 'orders', docRef.id), (snapshot) => {
        if (snapshot.exists()) {
          setCurrentOrder({ id: snapshot.id, ...snapshot.data() });
        }
      });
      setOrderListener(unsubscribe);
      
      setCart([]);
      setRicettaSelezionata(null);
      setShowPayment(false);
      setMandatoAccettato(false); // #6 — il mandato va riconferito a ogni ordine
      setDeliveryData({
        fullName: '',
        phone: '',
        address: '',
        city: '',
        zipCode: '',
        notes: '',
        customerLat: 0,
        customerLng: 0
      });
      setCurrentView('tracking');

      // Invia email conferma ordine
      try {
        await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/sendRiderEmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            to: currentUser.email,
            subject: `✅ Ordine confermato #${docRef.id.substring(0, 8)} — FarmaciaSubito`,
            html: `
              <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; color: #333;">
                <div style="background: #16a34a; padding: 32px; text-align: center; border-radius: 12px 12px 0 0;">
                  <h1 style="color: white; margin: 0; font-size: 24px;">💊 FarmaciaSubito</h1>
                  <p style="color: #bbf7d0; margin: 8px 0 0 0;">Ordine confermato!</p>
                </div>
                <div style="background: #f8fafc; padding: 32px; border-radius: 0 0 12px 12px; border: 1px solid #e2e8f0;">
                  <h2 style="color: #15803d; margin-top: 0;">✅ Il tuo ordine è stato ricevuto!</h2>
                  <p style="font-size: 15px; line-height: 1.6;">
                    Ciao <strong>${orderData.customerName || ''}</strong>, abbiamo ricevuto il tuo ordine e un rider si recherà presto nella farmacia più vicina a te per acquistare i prodotti richiesti.
                  </p>

                  <div style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 24px 0;">
                    <h3 style="margin: 0 0 16px 0; color: #374151; font-size: 16px;">📦 Riepilogo Ordine</h3>
                    <p style="margin: 0 0 8px 0; color: #6b7280; font-size: 13px;">ID Ordine: <strong>#${docRef.id.substring(0, 8)}</strong></p>
                    <table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
                      <thead>
                        <tr style="background: #f3f4f6;">
                          <th style="text-align: left; padding: 8px 12px; font-size: 13px; color: #374151;">Prodotto</th>
                          <th style="text-align: center; padding: 8px 12px; font-size: 13px; color: #374151;">Qtà</th>
                          <th style="text-align: right; padding: 8px 12px; font-size: 13px; color: #374151;">Prezzo</th>
                        </tr>
                      </thead>
                      <tbody>
                        ${orderData.items.map(item => `
                          <tr style="border-top: 1px solid #f3f4f6;">
                            <td style="padding: 8px 12px; font-size: 14px;">${item.name}</td>
                            <td style="padding: 8px 12px; font-size: 14px; text-align: center;">${item.quantity}</td>
                            <td style="padding: 8px 12px; font-size: 14px; text-align: right;">€${(item.price * item.quantity).toFixed(2)}</td>
                          </tr>
                        `).join('')}
                      </tbody>
                    </table>
                    <div style="border-top: 2px solid #e2e8f0; margin-top: 12px; padding-top: 12px;">
                      <div style="display: flex; justify-content: space-between; font-size: 13px; color: #6b7280; margin-bottom: 4px;">
                        <span>Subtotale</span><span>€${orderData.subtotal.toFixed(2)}</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; font-size: 13px; color: #6b7280; margin-bottom: 4px;">
                        <span>Spese servizio</span><span>€${orderData.serviceFee.toFixed(2)}</span>
                      </div>
                      <div style="display: flex; justify-content: space-between; font-size: 13px; color: #6b7280; margin-bottom: 4px;">
                        <span>Spese consegna</span><span>€${orderData.deliveryFee.toFixed(2)}</span>
                      </div>
                      ${orderData.mancia > 0 ? `
                      <div style="display: flex; justify-content: space-between; font-size: 13px; color: #6b7280; margin-bottom: 4px;">
                        <span>Mancia rider</span><span>€${orderData.mancia.toFixed(2)}</span>
                      </div>` : ''}
                      <div style="display: flex; justify-content: space-between; font-size: 16px; font-weight: bold; color: #15803d; margin-top: 8px;">
                        <span>Totale</span><span>€${total.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div style="background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 8px; padding: 16px; margin-bottom: 24px;">
                    <h3 style="margin: 0 0 8px 0; color: #15803d; font-size: 14px;">📍 Indirizzo di consegna</h3>
                    <p style="margin: 0; font-size: 14px; color: #374151;">${orderData.deliveryAddress}, ${orderData.deliveryCity} ${orderData.deliveryZipCode}</p>
                    ${orderData.deliveryNotes ? `<p style="margin: 4px 0 0 0; font-size: 13px; color: #6b7280;">Note: ${orderData.deliveryNotes}</p>` : ''}
                  </div>

                  <div style="text-align: center; margin: 28px 0;">
                    <a href="https://app.farmaciasubito.it" style="background: #16a34a; color: white; padding: 14px 32px; border-radius: 8px; text-decoration: none; font-weight: bold; font-size: 15px; display: inline-block;">
                      🛵 Segui il tuo ordine
                    </a>
                  </div>

                  <p style="font-size: 14px; color: #6b7280; line-height: 1.6;">
                    Per qualsiasi problema scrivici a <a href="mailto:info@farmaciasubito.it" style="color: #16a34a;">info@farmaciasubito.it</a>
                  </p>
                  <p style="font-size: 15px; margin-top: 24px;">Grazie per aver scelto FarmaciaSubito! 💊<br><strong>Il team FarmaciaSubito</strong></p>
                </div>
                <p style="text-align: center; font-size: 12px; color: #94a3b8; margin-top: 16px;">
                  FarmaciaSubito — Benevento | <a href="https://www.farmaciasubito.it/privacy-policy.html" style="color: #94a3b8;">Privacy Policy</a>
                </p>
              </div>
            `
          })
        });
      } catch (emailError) {
        console.warn('Email conferma ordine non inviata:', emailError);
      }

      alert('✅ Ordine creato con successo! Un rider raggiungerà la farmacia più vicina a te.');
      
    } catch (error) {
      console.error('Error creating order:', error);
      alert('❌ Errore durante la creazione dell\'ordine: ' + error.message);
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim()) return;
    
    setChatMessages([...chatMessages, {
      sender: 'customer',
      text: newMessage,
      time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    }]);
    setNewMessage('');
    
    setTimeout(() => {
      setChatMessages(prev => [...prev, {
        sender: 'pharmacist',
        text: 'Grazie per il messaggio! Ti risponderemo a breve.',
        time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
      }]);
    }, 1500);
  };

  const calculateDistance = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const getCoordinatesFromAddress = async (address, city, zipCode) => {
    const fullAddress = `${address}, ${zipCode} ${city}, Italy`;
    const apiKey = 'AIzaSyCpbBrZQcu6UaV-Ay29L5s0UUXmGyoxY78';
    
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(fullAddress)}&key=${apiKey}`
      );
      const data = await response.json();
      
      if (data.status === 'OK' && data.results.length > 0) {
        const location = data.results[0].geometry.location;
        return {
          lat: location.lat,
          lng: location.lng,
          success: true
        };
      } else {
        return { success: false, error: data.status };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      return { success: false, error: 'Errore geocoding' };
    }
  };

  useEffect(() => {
    const loadPharmacies = async () => {
      try {
        const q = query(collection(db, 'farmacie'), where('status', '==', 'active'));
        const snapshot = await getDocs(q);
        const fetchedPharmacies = [];
        
        snapshot.forEach((doc) => {
          const data = doc.data();
          fetchedPharmacies.push({
            id: doc.id,
            name: data.name,
            address: data.indirizzo || data.address,
            lat: data.lat,
            lng: data.lng,
            distance: '0.8 km',
            rating: 4.8,
            deliveryTime: '20-30 min',
            deliveryFee: 3.99
          });
        });
        
        setPharmacies(fetchedPharmacies);
      } catch (error) {
        console.error('Errore caricamento farmacie:', error);
      }
    };
    
    loadPharmacies();
  }, []);

  useEffect(() => {
    const loadProducts = async () => {
      // Applica il margine prudenziale al prezzo, conservando il prezzo base
      const applicaMargine = (p) => {
        const base = Number(p.price) || 0;
        const prezzoFinale = Math.round(base * (1 + margine / 100) * 100) / 100;
        return { ...p, prezzoBase: base, price: prezzoFinale };
      };
      try {
        const q = query(collection(db, 'prodotti'), where('disponibile', '==', true));
        const snapshot = await getDocs(q);
        const fetchedProducts = [];
        snapshot.forEach((doc) => {
          fetchedProducts.push(applicaMargine({ id: doc.id, ...doc.data() }));
        });
        setProducts(fetchedProducts);
      } catch (error) {
        // Fallback: carica tutti i prodotti
        try {
          const snapshot = await getDocs(collection(db, 'prodotti'));
          const fetchedProducts = [];
          snapshot.forEach((doc) => {
            fetchedProducts.push(applicaMargine({ id: doc.id, ...doc.data() }));
          });
          setProducts(fetchedProducts);
        } catch (e) {
          console.error('Errore caricamento prodotti:', e);
        }
      }
    };
    loadProducts();
  }, [margine]);

// CARICA ORDINI UTENTE DA FIREBASE
useEffect(() => {
  if (!currentUser?.uid) return;
  
  const loadOrders = async () => {
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      
      if (!snapshot.empty) {
        const latestOrder = { id: snapshot.docs[0].id, ...snapshot.docs[0].data() };
        setCurrentOrder(latestOrder);
        
        const unsubscribe = onSnapshot(doc(db, 'orders', latestOrder.id), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const prevStatus = currentOrder?.status;
          setCurrentOrder({ id: docSnap.id, ...data });
          // Mostra popup recensione quando ordine viene consegnato
          if (data.status === 'delivered' && prevStatus !== 'delivered' && data.riderId) {
            const alreadyReviewed = localStorage.getItem(`reviewed_${docSnap.id}`);
            if (!alreadyReviewed) {
              setTimeout(() => setShowReviewModal(true), 1500);
            }
          }
        }
      });
        setOrderListener(unsubscribe);

        // Conta ordini completati con carta per sbloccare contanti
        const completati = snapshot.docs.filter(d => {
          const o = d.data();
          return o.status === 'delivered' && 
                 o.paymentMethod !== 'cash' && 
                 o.paymentStatus === 'paid';
        }).length;
        setCompletedOrders(completati);
      }
    } catch (error) {
      console.error('❌ Errore caricamento ordini:', error);
    }
  };
  
  loadOrders();
}, [currentUser?.uid]);

  // ── POP-UP LEGALE: mostra l'informativa una volta al giorno dopo il login ──
  useEffect(() => {
    if (isLoggedIn) {
      const oggi = new Date().toDateString();
      const ultimaAccettazione = localStorage.getItem('fs_legal_accettato');
      if (ultimaAccettazione !== oggi) {
        setShowLegalPopup(true);
      }
    }
  }, [isLoggedIn]);

  const accettaInformativaLegale = () => {
    localStorage.setItem('fs_legal_accettato', new Date().toDateString());
    setShowLegalPopup(false);
  };

  // Registra su Firestore la ri-accettazione dei Termini aggiornati.
  // La prova resta sul documento utente: termsVersion + data.
  const accettaTerminiAggiornati = async () => {
    if (!currentUser?.uid) return;
    setSalvandoRiaccettazione(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        termsAccepted: true,
        termsAcceptedAt: new Date().toISOString(),
        termsVersion: TERMS_VERSION,
      });
      setShowRiaccettazioneToS(false);
    } catch (e) {
      console.error('Salvataggio ri-accettazione fallito:', e);
      alert('⚠️ Non è stato possibile salvare l\'accettazione. Verifica la connessione e riprova.');
    } finally {
      setSalvandoRiaccettazione(false);
    }
  };

  if (showSplash) {
    return <SplashScreen onDone={() => setShowSplash(false)} />;
  }

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-500 to-green-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-6">
            <Logo />
            <h2 className="text-xl font-bold text-gray-800 mt-2">
              {authMode === 'login' && 'Accedi al tuo account'}
              {authMode === 'register' && 'Crea un account'}
              {authMode === 'reset' && 'Recupera password'}
            </h2>
          </div>

          {/* TAB LOGIN / REGISTRATI */}
          {authMode !== 'reset' && (
            <div className="flex rounded-xl bg-gray-100 p-1 mb-6">
              <button
                onClick={() => { setAuthMode('login'); setLoginError(''); setConfirmPassword(''); }}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
                  authMode === 'login' ? 'bg-white text-green-600 shadow' : 'text-gray-500'
                }`}
              >
                Accedi
              </button>
              <button
                onClick={() => { setAuthMode('register'); setLoginError(''); }}
                className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
                  authMode === 'register' ? 'bg-white text-green-600 shadow' : 'text-gray-500'
                }`}
              >
                Registrati
              </button>
            </div>
          )}

          <div className="space-y-4">

            {/* RESET PASSWORD */}
            {authMode === 'reset' && (
              <>
                {resetSent ? (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-4 rounded-lg text-sm text-center">
                    ✅ Email inviata! Controlla la tua casella di posta e segui le istruzioni per reimpostare la password.
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-500 text-center">
                      Inserisci la tua email e ti manderemo un link per reimpostare la password.
                    </p>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                      <input
                        type="email"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        placeholder="tua@email.com"
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none"
                      />
                    </div>
                    {loginError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                        {loginError}
                      </div>
                    )}
                    <button
                      onClick={handlePasswordReset}
                      disabled={isLoading}
                      className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition disabled:bg-gray-400"
                    >
                      {isLoading ? 'Invio...' : '📧 Invia Email di Reset'}
                    </button>
                  </>
                )}
                <button
                  onClick={() => { setAuthMode('login'); setLoginError(''); setResetSent(false); }}
                  className="w-full text-green-600 font-semibold text-sm hover:underline"
                >
                  ← Torna al login
                </button>
              </>
            )}

            {/* LOGIN / REGISTRATI */}
            {authMode !== 'reset' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={(e) => setLoginEmail(e.target.value)}
                    placeholder="tua@email.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                  <input
                    type="password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    placeholder="••••••••"
                    onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none"
                  />
                  <p className="text-xs text-gray-400 mt-1">Minimo 6 caratteri</p>
                </div>

                {authMode === 'register' && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Conferma Password</label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="••••••••"
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 outline-none"
                    />
                  </div>
                )}

                {/* ACCETTAZIONE TERMINI E PRIVACY — obbligatoria */}
                {authMode === 'register' && (
                  <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={accettoTermini}
                        onChange={(e) => setAccettoTermini(e.target.checked)}
                        className="mt-0.5 w-5 h-5 accent-green-600 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-700 leading-relaxed">
                        Ho letto e accetto i{' '}
                        <a
                          href="https://www.farmaciasubito.it/termini-condizioni.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-green-700 font-semibold underline"
                        >
                          Termini e Condizioni
                        </a>
                        {' '}e la{' '}
                        <a
                          href="https://www.farmaciasubito.it/privacy-policy.html"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-green-700 font-semibold underline"
                        >
                          Privacy Policy
                        </a>
                        .
                        <span className="block text-xs text-gray-500 mt-1">
                          Obbligatorio per creare un account.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {/* CONSENSO MARKETING — facoltativo, non pre-spuntato (GDPR) */}
                {authMode === 'register' && (
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={consensoMarketing}
                        onChange={(e) => setConsensoMarketing(e.target.checked)}
                        className="mt-0.5 w-5 h-5 accent-green-600 flex-shrink-0"
                      />
                      <span className="text-sm text-gray-600 leading-relaxed">
                        Desidero ricevere <strong>offerte, promozioni e novità</strong> da FarmaciaSubito.
                        <span className="block text-xs text-gray-400 mt-1">
                          Facoltativo. Le notifiche sui tuoi ordini le riceverai comunque.
                          Puoi cambiare idea quando vuoi dal tuo Profilo.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                {loginError && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {loginError}
                  </div>
                )}

                <button
                  onClick={handleLogin}
                  disabled={isLoading}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-bold text-lg hover:bg-green-700 transition shadow-lg disabled:bg-gray-400"
                >
                  {isLoading ? 'Caricamento...' : authMode === 'login' ? '🔑 Accedi' : '✅ Crea Account'}
                </button>

                {authMode === 'login' && (
                  <button
                    onClick={() => { setAuthMode('reset'); setLoginError(''); }}
                    className="w-full text-green-600 font-semibold text-sm hover:underline"
                  >
                    Password dimenticata?
                  </button>
                )}

                <p className="text-xs text-gray-400 text-center">
                  {authMode === 'login'
                    ? 'Non hai un account? Clicca su Registrati'
                    : 'Hai già un account? Clicca su Accedi'}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── POP-UP BLOCCANTE — RI-ACCETTAZIONE TERMINI AGGIORNATI ──
  // Ha priorità sull'informativa legale: chi non ha accettato la versione
  // corrente non può usare l'app.
  if (showRiaccettazioneToS) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="bg-green-600 p-6 rounded-t-2xl text-center sticky top-0">
            <div className="flex items-center justify-center gap-2 mb-1">
              <FileText className="text-white" size={24} />
              <h1 className="text-white text-xl font-bold">Termini aggiornati</h1>
            </div>
            <p className="text-green-100 text-sm">Aggiornamento del 19 luglio 2026</p>
          </div>
          <div className="p-6">
            <p className="text-gray-700 text-sm leading-relaxed mb-4">
              Abbiamo aggiornato i <strong>Termini e Condizioni</strong> del servizio. Per continuare a usare
              FarmaciaSubito è necessario prenderne visione e accettarli.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-sm font-bold text-gray-800 mb-2">Cosa è cambiato</p>
              <ul className="text-sm text-gray-700 space-y-2 list-disc list-inside">
                <li>
                  <strong>Farmaci da frigo:</strong> non trasportiamo medicinali che richiedono la catena del
                  freddo (2-8°C), come insuline e vaccini. Se un ordine li contiene, la parte interessata viene
                  annullata e rimborsata.
                </li>
                <li>
                  <strong>Responsabilità:</strong> abbiamo chiarito il nostro perimetro. Rispondiamo del
                  trasporto, non dei difetti di fabbricazione o degli errori di prescrizione. Abbiamo inoltre
                  <strong> rimosso ogni limite</strong> di responsabilità sui danni alla persona.
                </li>
                <li>
                  <strong>Diritto di recesso:</strong> i prodotti sigillati non sono restituibili per motivi
                  igienici una volta aperti. Resta il recesso sui prodotti con sigillo integro.
                </li>
                <li>
                  <strong>Ruolo del rider:</strong> il rider è un trasportatore, non un professionista sanitario.
                  La verifica tra ricetta e farmaco spetta al farmacista.
                </li>
              </ul>
            </div>

            <p className="text-xs text-gray-500 mb-5">
              Il riepilogo qui sopra è a scopo informativo. Fa fede il testo integrale dei Termini.
            </p>

            <a
              href="https://www.farmaciasubito.it/termini-condizioni.html"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full text-center border-2 border-green-600 text-green-700 font-semibold py-3 rounded-xl mb-3 hover:bg-green-50 transition-colors"
            >
              Leggi i Termini e Condizioni completi
            </a>

            <button
              onClick={accettaTerminiAggiornati}
              disabled={salvandoRiaccettazione}
              className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              {salvandoRiaccettazione ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Salvataggio...
                </>
              ) : (
                <>
                  <CheckCircle size={20} />
                  Ho letto e accetto i Termini aggiornati
                </>
              )}
            </button>

            <button
              onClick={handleLogout}
              className="w-full text-gray-500 text-sm py-3 mt-2 hover:text-gray-700 transition-colors"
            >
              Esci
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── POP-UP LEGALE BLOCCANTE — Informativa mandato/logistica ──
  if (showLegalPopup) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 to-green-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="bg-green-600 p-6 rounded-t-2xl text-center sticky top-0">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Info className="text-white" size={24} />
              <h1 className="text-white text-xl font-bold">Informativa all'Utente</h1>
            </div>
            <p className="text-green-100 text-sm">FarmaciaSubito</p>
          </div>
          <div className="p-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
              <p className="text-gray-800 text-sm leading-relaxed">
                <strong>FarmaciaSubito</strong> è una piattaforma di servizi tecnologici e di logistica a domicilio.
                FarmaciaSubito <strong>NON vende</strong>, <strong>NON distribuisce</strong> e <strong>NON pubblicizza</strong> farmaci o medicinali.
              </p>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              Utilizzando questo servizio, l'utente conferisce un <strong>mandato di rappresentanza e delega formale</strong> al
              rider di FarmaciaSubito affinché si rechi presso la farmacia fisica di turno della città, effettui l'acquisto
              e il pagamento al POS o per contanti per suo conto, e trasporti i prodotti all'indirizzo indicato.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              Il contratto di compravendita e lo scontrino fiscale vengono emessi <strong>esclusivamente dalla farmacia
              fisica</strong> al momento del ritiro.
            </p>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">
              <strong>Prezzi.</strong> L'importo che vedi nell'app è il <strong>prezzo finale</strong> che paghi: nessun
              costo aggiuntivo dopo l'ordine, nessun conguaglio. I prezzi mostrati nel catalogo <strong>possono essere
              superiori a quelli esposti in farmacia</strong>: includono infatti, oltre al costo del prodotto, la quota
              relativa al servizio di ricerca, acquisto e trasporto svolto per tuo conto. Le differenze di prezzo
              riscontrate in farmacia restano a nostro carico, mai tue. Il catalogo comprende esclusivamente prodotti
              a <strong>prezzo libero</strong> (farmaci da banco e parafarmaci).
            </p>
            <p className="text-gray-700 text-sm leading-relaxed mb-5">
              <strong>Dati della ricetta.</strong> Se richiedi il ritiro di farmaci con ricetta, i dati sanitari che fornisci
              (codice NRE o foto della prescrizione) vengono usati solo per il ritiro in farmacia e <strong>cancellati subito
              dopo la consegna</strong>. La dispensazione resta atto esclusivo del farmacista.
            </p>
            <button
              onClick={accettaInformativaLegale}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-4 rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              <CheckCircle size={20} />
              Accetto e Continua
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showDeliveryForm) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Dati di Consegna</h2>
              <button
                onClick={() => setShowDeliveryForm(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Nome Completo *</label>
                <input
                  type="text"
                  value={deliveryData.fullName}
                  onChange={(e) => setDeliveryData({...deliveryData, fullName: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                  placeholder="Mario Rossi"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Telefono *</label>
                <input
                  type="tel"
                  value={deliveryData.phone}
                  onChange={(e) => setDeliveryData({...deliveryData, phone: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                  placeholder="+39 333 1234567"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Indirizzo di Consegna *</label>
                <input
                  type="text"
                  value={deliveryData.address}
                  onChange={(e) => setDeliveryData({...deliveryData, address: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                  placeholder="Via Roma 123"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">Città *</label>
                  <input
                    type="text"
                    value={deliveryData.city}
                    onChange={(e) => setDeliveryData({...deliveryData, city: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                    placeholder="Milano"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">CAP *</label>
                  <input
                    type="text"
                    value={deliveryData.zipCode}
                    onChange={(e) => setDeliveryData({...deliveryData, zipCode: e.target.value})}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                    placeholder="20100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Note per il Rider (opzionale)</label>
                <textarea
                  value={deliveryData.notes}
                  onChange={(e) => setDeliveryData({...deliveryData, notes: e.target.value})}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none"
                  rows="3"
                  placeholder="Es: Citofono 5, primo piano..."
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Codice Fiscale <span className="text-gray-400 font-normal">(opzionale - per detrazione farmaci)</span>
                </label>
                <input
                  type="text"
                  placeholder="Es. RSSMRA80A01H501U"
                  value={deliveryData.codiceFiscale}
                  onChange={(e) => setDeliveryData({...deliveryData, codiceFiscale: e.target.value.toUpperCase()})}
                  maxLength={16}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-300 focus:border-green-500 outline-none uppercase"
                />
                <p className="text-xs text-gray-400 mt-1">📋 Necessario per scaricare i farmaci dalla dichiarazione dei redditi</p>
              </div>
            </div>

            {/* #6 — MANDATO DI RAPPRESENTANZA — checkbox NON preselezionata, obbligatoria a ogni ordine */}
            <div className="mt-6 bg-amber-50 border-2 border-amber-200 rounded-xl p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={mandatoAccettato}
                  onChange={(e) => setMandatoAccettato(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-green-600 flex-shrink-0 cursor-pointer"
                />
                <span className="text-sm text-gray-800 leading-relaxed">
                  <strong>Conferisco mandato di rappresentanza al rider per il ritiro dei farmaci.</strong>
                  <span className="block text-xs text-gray-600 mt-1">
                    Autorizzo il rider di FarmaciaSubito ad acquistare per mio conto i prodotti indicati presso la
                    farmacia e a trasportarli all'indirizzo di consegna. Il contratto di compravendita e lo scontrino
                    fiscale sono emessi dalla farmacia. Il rider è un mero trasportatore: la dispensazione resta atto
                    esclusivo del farmacista.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowDeliveryForm(false)}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-300 transition"
              >
                Indietro
              </button>
              <button
                onClick={async () => {
                  // #6 — Il mandato deve essere conferito esplicitamente prima di procedere
                  if (!mandatoAccettato) {
                    alert('⚠️ Per procedere devi conferire il mandato di rappresentanza al rider (casella in fondo al modulo).');
                    return;
                  }
                  if (!deliveryData.fullName || !deliveryData.phone || !deliveryData.address || !deliveryData.city || !deliveryData.zipCode) {
                    alert('⚠️ Compila tutti i campi obbligatori!');
                    return;
                  }
                  
                  // CONTROLLO CAP — da impostazioni Firestore
                  const CAP_ACCETTATI = zonaImpostazioni.capAccettati || ['82100'];
                  if (!CAP_ACCETTATI.includes(deliveryData.zipCode.trim())) {
                    alert(`❌ Spiacenti! Al momento consegniamo solo nel Comune di Benevento (CAP ${CAP_ACCETTATI.join(', ')}).\n\nIl CAP inserito (${deliveryData.zipCode}) non è nella nostra zona di copertura.`);
                    return;
                  }

                  const coords = await getCoordinatesFromAddress(
                    deliveryData.address, 
                    deliveryData.city, 
                    deliveryData.zipCode
                  );

                  if (!coords.success) {
                    alert('❌ Indirizzo non valido! Verifica i dati inseriti.');
                    return;
                  }

                  const customerLat = coords.lat;
                  const customerLng = coords.lng;
                  
                  setDeliveryData({
                    ...deliveryData,
                    customerLat: customerLat,
                    customerLng: customerLng
                  });

                  // CONTROLLO ZONA COPERTURA — max 5km da Benevento centro
                  const BENEVENTO_LAT = zonaImpostazioni.latitudine || 41.1297;
                  const BENEVENTO_LNG = zonaImpostazioni.longitudine || 14.7824;
                  const MAX_KM = zonaImpostazioni.raggioKm || 5;

                  const dLat = (customerLat - BENEVENTO_LAT) * Math.PI / 180;
                  const dLng = (customerLng - BENEVENTO_LNG) * Math.PI / 180;
                  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                    Math.cos(BENEVENTO_LAT * Math.PI / 180) * Math.cos(customerLat * Math.PI / 180) *
                    Math.sin(dLng/2) * Math.sin(dLng/2);
                  const distanzaKm = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

                  if (distanzaKm > MAX_KM) {
                    alert(`❌ Spiacenti! Al momento consegniamo solo entro ${MAX_KM} km da Benevento centro.\n\nIl tuo indirizzo è a ${distanzaKm.toFixed(1)} km dalla zona di copertura.`);
                    return;
                  }

                  setShowDeliveryForm(false);
                  setShowPayment(true);
                }}
                className="flex-1 bg-green-600 text-white py-3 rounded-xl font-semibold hover:bg-green-700 transition"
              >
                Continua al Pagamento
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showPayment) {
    // Contanti disponibili solo dopo il primo ordine consegnato e pagato con carta.
    // Protezione anti-frode: il primo ordine deve essere tracciabile.
    const canUseCash = completedOrders >= 1;
    const cartHasPrescription = cart.some(item => item.prescription);

    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Pagamento</h2>
              <button
                onClick={() => setShowPayment(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} />
              </button>
            </div>

            {cartHasPrescription && (
              <div className="mb-6 border-2 border-red-200 rounded-2xl p-4 bg-red-50">
                <h3 className="font-bold text-red-800 mb-1 flex items-center gap-2">📋 Ricetta obbligatoria</h3>
                <p className="text-sm text-red-600 mb-3">Il tuo ordine contiene prodotti che richiedono ricetta medica.</p>
                {ricettaSelezionata ? (
                  <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-red-200">
                    <span className="text-2xl">{ricettaSelezionata.tipo === 'application/pdf' ? '📄' : '🖼️'}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 text-sm truncate">{ricettaSelezionata.nome}</p>
                      <p className="text-xs text-green-600 font-bold">✅ Ricetta selezionata</p>
                    </div>
                    <button onClick={() => setRicettaSelezionata(null)} className="text-red-500 text-xs font-bold">Cambia</button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {ricette && ricette.length > 0 ? (
                      ricette.map(r => (
                        <button key={r.id} onClick={() => setRicettaSelezionata(r)}
                          className="w-full flex items-center gap-3 bg-white rounded-xl p-3 border border-red-200 hover:border-red-400 transition text-left">
                          <span className="text-xl">{r.tipo === 'application/pdf' ? '📄' : '🖼️'}</span>
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 text-sm truncate">{r.nome}</p>
                            <p className="text-xs text-gray-500">{new Date(r.createdAt).toLocaleDateString('it-IT')}</p>
                          </div>
                          <span className="text-xs text-red-600 font-bold">Seleziona →</span>
                        </button>
                      ))
                    ) : (
                      <div className="bg-white rounded-xl p-3 border border-red-200 text-center">
                        <p className="text-sm text-gray-600 mb-2">Nessuna ricetta salvata.</p>
                        <button onClick={() => { setShowPayment(false); setCurrentView('profile'); }}
                          className="text-red-600 font-bold text-sm underline">
                          Vai a "Le mie Ricette" per caricarla
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mb-6">
              <h3 className="font-bold text-gray-800 mb-3">Seleziona metodo di pagamento</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setPaymentMethod('card')}
                  className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-3 ${
                    paymentMethod === 'card'
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-300 hover:border-green-300'
                  }`}
                >
                  <CreditCard size={24} className={paymentMethod === 'card' ? 'text-green-600' : 'text-gray-600'} />
                  <div className="flex-1 text-left">
                    <p className="font-bold text-gray-800">Carta di Credito/Debito</p>
                    <p className="text-sm text-gray-500">Paga con Visa, Mastercard, Amex</p>
                  </div>
                  {paymentMethod === 'card' && (
                    <CheckCircle className="text-green-600" size={24} />
                  )}
                </button>

                <button
                  onClick={() => canUseCash && setPaymentMethod('cash')}
                  disabled={!canUseCash}
                  className={`w-full p-4 rounded-xl border-2 transition flex items-center gap-3 ${
                    paymentMethod === 'cash'
                      ? 'border-green-500 bg-green-50'
                      : canUseCash
                      ? 'border-gray-300 hover:border-green-300'
                      : 'border-gray-200 bg-gray-100 cursor-not-allowed'
                  }`}
                >
                  <div className="w-6 h-6 flex items-center justify-center">
                    <span className={`text-2xl ${!canUseCash ? 'text-gray-400' : paymentMethod === 'cash' ? 'text-green-600' : 'text-gray-600'}`}>💵</span>
                  </div>
                  <div className="flex-1 text-left">
                    <p className={`font-bold ${!canUseCash ? 'text-gray-400' : 'text-gray-800'}`}>
                      Contanti alla consegna
                    </p>
                    <p className={`text-sm ${!canUseCash ? 'text-gray-400' : 'text-gray-500'}`}>
                      {canUseCash 
                        ? 'Paga in contanti al rider' 
                        : 'Disponibile dopo il primo ordine pagato con carta'}
                    </p>
                  </div>
                  {paymentMethod === 'cash' && (
                    <CheckCircle className="text-green-600" size={24} />
                  )}
                </button>
              </div>
            </div>

            {paymentMethod === 'card' && (
              <Elements stripe={stripePromise}>
                <CheckoutForm 
                  total={getFinalTotal()}
                  onSuccess={handlePaymentSuccess}
                  onCancel={() => setShowPayment(false)}
                />
              </Elements>
            )}

            {paymentMethod === 'cash' && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border-2 border-yellow-200 p-4 rounded-xl">
                  <p className="text-sm text-yellow-800 font-semibold">
                    ⚠️ Prepara l'importo esatto: €{(getCartTotal() + 3.99).toFixed(2)}
                  </p>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="flex justify-between items-center text-lg font-bold">
                    <span>Totale da pagare in contanti:</span>
                    <span className="text-green-600">€{(getCartTotal() + 3.99).toFixed(2)}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowPayment(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
                  >
                    Annulla
                  </button>
                  <button
                    onClick={() => {
                      if (cartHasPrescription && !ricettaSelezionata) { alert('⚠️ Seleziona una ricetta per procedere.'); return; }
                      handlePaymentSuccess({ paymentMethodId: 'cash', status: 'pending' });
                    }}
                    className="flex-1 bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition flex items-center justify-center gap-2"
                  >
                    💵 Conferma Ordine
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const handleRicettaRequestUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/heic,image/heif,image/*,application/pdf';
    input.style.position = 'fixed';
    input.style.top = '-100px';
    input.style.opacity = '0';
    document.body.appendChild(input);
    input.addEventListener('change', async (e) => {
      const file = e.target.files[0];
      document.body.removeChild(input);
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { alert('⚠️ File troppo grande. Massimo 10MB.'); return; }
      setUploadingRicettaRequest(true);
      try {
        const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
        const storage = getStorage();
        const ext = file.name.split('.').pop();
        const storageRef = ref(storage, `ricette-richieste/${currentUser?.uid || 'anonimo'}/${Date.now()}.${ext}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        setRicettaRequestData(prev => ({ ...prev, ricettaUrl: url, ricettaNome: file.name }));
      } catch (e) {
        alert('❌ Errore upload: ' + e.message);
      } finally {
        setUploadingRicettaRequest(false);
      }
    });
    input.addEventListener('cancel', () => document.body.removeChild(input));
    input.click();
  };

  const inviaRicettaRequest = async () => {
    // Sicurezza: servizio sospeso → niente nuove richieste ricetta.
    if (!servizioAttivo) {
      alert('🕐 Il servizio è temporaneamente sospeso. Non è possibile inviare nuove richieste in questo momento.');
      return;
    }
    if (!ricettaRequestData.fullName || !ricettaRequestData.phone || !ricettaRequestData.address || !ricettaRequestData.city) {
      alert('⚠️ Compila tutti i dati di consegna.');
      return;
    }
    if (!ricettaRequestData.ricettaUrl) { alert('⚠️ Carica prima la ricetta.'); return; }
    if (!ricettaPaymentDone || !ricettaPaymentInfo) { alert('⚠️ Registra prima la carta per procedere.'); return; }

    // Controllo orario: se il servizio è chiuso, serve un preordine con slot scelto
    if (!isServiceOpen() && !ricettaIsPreOrder) {
      alert('⚠️ Il servizio è chiuso in questo momento (orari: 09:00-13:00 e 16:00-20:30).\n\nPuoi programmare la consegna scegliendo un orario nella sezione Preordine qui sotto.');
      return;
    }
    if (ricettaIsPreOrder && !ricettaSelectedSlot && getSlotDisponibili().length > 0) {
      alert('⚠️ Seleziona un orario di consegna per il preordine.');
      return;
    }

    // Controllo zona copertura
    const coords = await getCoordinatesFromAddress(
      ricettaRequestData.address,
      ricettaRequestData.city,
      ricettaRequestData.zipCode || ''
    );
    if (!coords.success) {
      alert('❌ Indirizzo non valido! Verifica i dati inseriti.');
      return;
    }
    const BENEVENTO_LAT = zonaImpostazioni.latitudine || 41.1297;
    const BENEVENTO_LNG = zonaImpostazioni.longitudine || 14.7824;
    const MAX_KM = zonaImpostazioni.raggioKm || 5;
    const dLat = (coords.lat - BENEVENTO_LAT) * Math.PI / 180;
    const dLng = (coords.lng - BENEVENTO_LNG) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(BENEVENTO_LAT * Math.PI / 180) * Math.cos(coords.lat * Math.PI / 180) *
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const distanzaKm = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    if (distanzaKm > MAX_KM) {
      alert(`❌ Spiacenti! Al momento consegniamo solo entro ${MAX_KM} km da Benevento centro.\n\nIl tuo indirizzo è a ${distanzaKm.toFixed(1)} km dalla zona di copertura.`);
      return;
    }

    try {
      // 1. Crea la richiesta ricetta
      const richiestaRef = await addDoc(collection(db, 'richieste-ricetta'), {
        userId: currentUser?.uid || null,
        userEmail: currentUser?.email || '',
        customerName: ricettaRequestData.fullName,
        customerPhone: ricettaRequestData.phone,
        deliveryAddress: ricettaRequestData.address,
        deliveryCity: ricettaRequestData.city,
        deliveryZipCode: ricettaRequestData.zipCode || '',
        deliveryLat: coords.lat,
        deliveryLng: coords.lng,
        ricettaUrl: ricettaRequestData.ricettaUrl,
        ricettaNome: ricettaRequestData.ricettaNome,
        note: ricettaRequestData.note || '',
        status: 'pending',
        paymentMethod: 'card',
        paymentMethodId: ricettaPaymentInfo.paymentMethodId,
        stripeCustomerId: ricettaPaymentInfo.stripeCustomerId || null,
        servizioPagato: 4.99,
        isPreOrder: ricettaIsPreOrder,
        scheduledFor: ricettaIsPreOrder ? (ricettaSelectedSlot || getNextSlot())?.date.toISOString() : null,
        scheduledLabel: ricettaIsPreOrder ? (ricettaSelectedSlot || getNextSlot())?.label : null,
        createdAt: new Date().toISOString()
      });

      // 2. Avvia subito il flusso automatico lato server: addebito €4,99 +
      //    creazione ordine + assegnazione rider (come un ordine normale).
      const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/completaRichiestaRicetta', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ richiestaId: richiestaRef.id })
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        throw new Error(data.error || 'Errore durante la creazione dell\'ordine');
      }

      setRicettaRequestSent(true);
    } catch (e) {
      alert('❌ Errore: ' + e.message);
    }
  };

  // MODAL RICHIESTA RICETTA
  if (showRicettaRequest) return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => { setShowRicettaRequest(false); setRicettaRequestSent(false); setRicettaRequestData({ note: '', ricettaUrl: null, ricettaNome: null, fullName: '', phone: '', address: '', city: '', zipCode: '' }); setRicettaPaymentDone(false); setRicettaPaymentInfo(null); }}
          className="p-2 hover:bg-gray-100 rounded-xl">
          <ChevronLeft size={22} className="text-gray-600" />
        </button>
        <h2 className="text-xl font-black text-gray-900">Ordina con Ricetta</h2>
      </div>

      <div className="p-4 space-y-4">
        {ricettaRequestSent ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">✅</div>
            <h3 className="text-2xl font-black text-gray-900 mb-2">Richiesta inviata!</h3>
            <p className="text-gray-500 mb-2">Abbiamo ricevuto la tua ricetta e addebitato €4,99.</p>
            <p className="text-gray-500 mb-6">Un rider è stato incaricato: ritirerà il farmaco in farmacia e te lo consegnerà. Il costo del farmaco si paga al rider alla consegna.</p>
            <button
              onClick={() => { setShowRicettaRequest(false); setRicettaRequestSent(false); setRicettaRequestData({ note: '', ricettaUrl: null, ricettaNome: null }); }}
              className="bg-green-600 text-white px-8 py-3 rounded-xl font-bold"
            >
              Torna alla Home
            </button>
          </div>
        ) : (
          <>
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
              <p className="text-sm font-bold text-red-800">📋 Come funziona</p>
              <div className="mt-2 space-y-1">
                {['Carica la foto della tua ricetta medica', 'Il nostro team la esamina e acquista il farmaco in farmacia', 'Il rider consegna a casa tua', 'Paghi in contanti al rider'].map((s, i) => (
                  <p key={i} className="text-xs text-red-700">✓ {s}</p>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-4">
              <p className="font-bold text-gray-900 mb-3">1. Dati di consegna *</p>
              <div className="space-y-3">
                <input type="text"
                  value={ricettaRequestData.fullName}
                  onChange={e => setRicettaRequestData(prev => ({ ...prev, fullName: e.target.value }))}
                  placeholder="Nome e Cognome *"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-400 outline-none text-sm"
                />
                <input type="tel"
                  value={ricettaRequestData.phone}
                  onChange={e => setRicettaRequestData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="Telefono * (es. +39 333 1234567)"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-400 outline-none text-sm"
                />
                <input type="text"
                  value={ricettaRequestData.address}
                  onChange={e => setRicettaRequestData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Indirizzo di consegna * (es. Via Roma 123)"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-400 outline-none text-sm"
                />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text"
                    value={ricettaRequestData.city}
                    onChange={e => setRicettaRequestData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Città *"
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-400 outline-none text-sm"
                  />
                  <input type="text"
                    value={ricettaRequestData.zipCode}
                    onChange={e => setRicettaRequestData(prev => ({ ...prev, zipCode: e.target.value }))}
                    placeholder="CAP"
                    className="px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-red-400 outline-none text-sm"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-md p-4">
              <p className="font-bold text-gray-900 mb-3">2. Carica la ricetta *</p>
              {ricettaRequestData.ricettaUrl ? (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                  <span className="text-2xl">📋</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm truncate">{ricettaRequestData.ricettaNome}</p>
                    <p className="text-xs text-green-600 font-bold">✅ Ricetta caricata</p>
                  </div>
                  <button onClick={() => setRicettaRequestData(prev => ({ ...prev, ricettaUrl: null, ricettaNome: null }))}
                    className="text-red-500 text-xs font-bold">Cambia</button>
                </div>
              ) : (
                <button onClick={handleRicettaRequestUpload} disabled={uploadingRicettaRequest}
                  className="w-full border-2 border-dashed border-red-300 rounded-xl py-6 text-center hover:border-red-500 transition disabled:opacity-50">
                  {uploadingRicettaRequest ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-500"></div>
                      <span className="text-red-600 font-semibold">Caricamento...</span>
                    </div>
                  ) : (
                    <>
                      <div className="text-4xl mb-2">📸</div>
                      <p className="font-bold text-red-600">Tocca per caricare la ricetta</p>
                      <p className="text-xs text-gray-500 mt-1">JPG, PNG, PDF — max 10MB</p>
                    </>
                  )}
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl shadow-md p-4">
              <p className="font-bold text-gray-900 mb-3">3. Note aggiuntive (opzionale)</p>
              <textarea
                value={ricettaRequestData.note}
                onChange={(e) => setRicettaRequestData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Es: Farmaco urgente, marca preferita, dosaggio specifico..."
                className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 focus:ring-2 focus:ring-red-500 outline-none text-sm resize-none"
                rows={3}
              />
            </div>

            {/* PAGAMENTO €4,99 */}
            <div className="bg-white rounded-2xl shadow-md p-4">
              <p className="font-bold text-gray-900 mb-3">4. Registra la carta *</p>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-3">
                <p className="text-sm text-blue-800 font-bold">💳 Servizio di consegna con ricetta: €4,99</p>
                <p className="text-xs text-blue-600 mt-1">I €4,99 vengono addebitati all'invio della richiesta. Il costo del farmaco si paga al rider alla consegna.</p>
              </div>
              {ricettaPaymentDone ? (
                <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="font-bold text-green-800 text-sm">Carta registrata!</p>
                    <p className="text-xs text-green-600">Verrà addebitata (€4,99) all'invio della richiesta</p>
                  </div>
                </div>
              ) : (
                <Elements stripe={stripePromise}>
                  <RicettaCheckoutForm
                    userEmail={currentUser?.email || ''}
                    userId={currentUser?.uid || null}
                    onSuccess={(paymentInfo) => {
                      setRicettaPaymentDone(true);
                      setRicettaPaymentInfo(paymentInfo);
                    }}
                  />
                </Elements>
              )}
            </div>

            {/* PREORDINE RICETTA — mostrato quando il servizio è chiuso */}
            {!isServiceOpen() && (
              <div className="bg-orange-50 border-2 border-orange-200 rounded-2xl p-4 mb-3">
                <p className="text-sm font-black text-orange-800 mb-1">🕐 Servizio chiuso in questo momento</p>
                <p className="text-xs text-orange-600 mb-3">Orari: 09:00-13:00 e 16:00-20:30. Puoi programmare la consegna per il prossimo orario disponibile.</p>
                <label className="flex items-center gap-2 mb-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ricettaIsPreOrder}
                    onChange={e => { setRicettaIsPreOrder(e.target.checked); if (!e.target.checked) setRicettaSelectedSlot(null); }}
                    className="w-5 h-5 accent-orange-600"
                  />
                  <span className="text-sm font-bold text-gray-800">Programma la consegna (preordine)</span>
                </label>
                {ricettaIsPreOrder && (
                  <div className="space-y-2">
                    {getSlotDisponibili().length > 0 ? (
                      getSlotDisponibili().map((slot, idx) => (
                        <button
                          key={idx}
                          onClick={() => setRicettaSelectedSlot(slot)}
                          className={`w-full text-left px-3 py-2 rounded-xl border-2 transition ${
                            ricettaSelectedSlot?.label === slot.label
                              ? 'border-orange-500 bg-orange-100'
                              : 'border-gray-200 bg-white'
                          }`}
                        >
                          <span className="text-sm font-semibold text-gray-800">🕐 {slot.label}</span>
                        </button>
                      ))
                    ) : (
                      <p className="text-xs text-gray-500">Nessuno slot disponibile al momento. Riprova più tardi.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <button
              onClick={inviaRicettaRequest}
              disabled={!ricettaRequestData.ricettaUrl || !ricettaPaymentDone || (!isServiceOpen() && !ricettaIsPreOrder)}
              className="w-full bg-red-600 text-white py-4 rounded-2xl font-black text-lg hover:bg-red-700 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {!isServiceOpen() && ricettaIsPreOrder ? '📅 Programma Richiesta' : '📋 Invia Richiesta'}
            </button>
          </>
        )}
      </div>
    </div>
  );

  const HomeView = () => {
    const nomeUtente = (currentUser?.displayName || currentUser?.email || 'Benvenuto').split('@')[0].split(' ')[0];
    const nomeCapitalizzato = nomeUtente.charAt(0).toUpperCase() + nomeUtente.slice(1);
    const prodottiPopolari = (products || []).slice(0, 4);

    return (
      <div className="space-y-5 pb-24">

        {/* HEADER ARIOSO — saluto */}
        <div className="flex items-center justify-between pt-1">
          <div>
            <p className="text-sm text-gray-400">Buongiorno</p>
            <p className="text-xl font-semibold text-gray-800">{nomeCapitalizzato}</p>
          </div>
          <button
            onClick={() => setCurrentView('profile')}
            className="w-11 h-11 rounded-full bg-green-50 flex items-center justify-center text-green-700"
          >
            <User size={20} />
          </button>
        </div>

        {/* RICERCA */}
        <button
          onClick={() => setCurrentView('products')}
          className="w-full h-12 rounded-2xl bg-white border border-green-100 flex items-center px-4 gap-3 text-left"
        >
          <Search size={18} className="text-green-300" />
          <span className="text-gray-400 text-sm">Cerca un farmaco o prodotto…</span>
        </button>

        {/* BANNER BENVENUTO */}
        <div className="rounded-3xl bg-green-600 p-5 relative overflow-hidden shadow-lg">
          <div className="absolute -right-6 -top-6 w-28 h-28 rounded-full bg-white opacity-10"></div>
          <div className="relative">
            <h2 className="text-white text-lg font-semibold leading-snug">La tua farmacia,<br/>a casa in poco tempo</h2>
            <p className="text-green-100 text-xs mt-1.5">Un rider ritira e ti consegna tutto</p>
            <button
              onClick={() => setCurrentView('products')}
              className="mt-3 inline-flex items-center gap-1.5 bg-white text-green-600 rounded-xl px-4 py-2 font-semibold text-sm active:scale-95 transition-transform"
            >
              Ordina ora <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* DUE AZIONI PRINCIPALI */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setCurrentView('products')}
            className="bg-white border border-green-50 rounded-2xl p-4 text-center active:scale-95 transition-transform"
          >
            <div className="w-11 h-11 mx-auto rounded-xl bg-green-50 flex items-center justify-center text-green-700 text-2xl">💊</div>
            <p className="mt-2 text-sm font-semibold text-gray-800">Catalogo</p>
          </button>
          <button
            onClick={() => setShowRicettaRequest(true)}
            className="bg-white border border-orange-50 rounded-2xl p-4 text-center active:scale-95 transition-transform"
          >
            <div className="w-11 h-11 mx-auto rounded-xl bg-orange-50 flex items-center justify-center text-orange-700 text-2xl">📋</div>
            <p className="mt-2 text-sm font-semibold text-gray-800">Con ricetta</p>
          </button>
        </div>

        {/* PRODOTTI POPOLARI */}
        {prodottiPopolari.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-semibold text-gray-800">Prodotti popolari</span>
              <button onClick={() => setCurrentView('products')} className="text-xs text-green-600 font-semibold">Vedi tutti</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {prodottiPopolari.map(product => (
                <div key={product.id} className="bg-white border border-green-50 rounded-2xl p-3">
                  <div className="h-24 rounded-xl bg-green-50 flex items-center justify-center overflow-hidden">
                    {product.imageUrl
                      ? <img src={product.imageUrl} alt={product.name} className="w-full h-full object-contain" />
                      : <span className="text-3xl">💊</span>}
                  </div>
                  <p className="mt-2 text-xs font-semibold text-gray-800 line-clamp-2 min-h-[2rem]">{product.name}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-sm text-green-600 font-bold">€{(product.price || 0).toFixed(2)}</span>
                    <button
                      onClick={() => addToCart(product)}
                      className="w-7 h-7 rounded-lg bg-green-600 text-white flex items-center justify-center active:scale-90 transition-transform"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* BANNER SUGGERISCI PRODOTTO (mantenuto, discreto) */}
        <button
          onClick={() => setCurrentView('suggest')}
          className="w-full bg-green-50 rounded-2xl p-4 flex items-center gap-3 text-left active:scale-95 transition-transform"
        >
          <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center text-2xl flex-shrink-0">💡</div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800">Non trovi quello che cerchi?</p>
            <p className="text-xs text-gray-500">Suggeriscici il prodotto, lo aggiungiamo per te</p>
          </div>
          <ChevronRight className="text-gray-400" size={20} />
        </button>

      </div>
    );
  };

  const ProductsView = () => {
    const [localSearch, setLocalSearch] = React.useState('');
    const [localCategory, setLocalCategory] = React.useState('all');
    const [localFavorites, setLocalFavorites] = React.useState(new Set());

    React.useEffect(() => {
      if (!currentUser?.uid) return;
      getDocs(query(collection(db, 'users', currentUser.uid, 'favorites'))).then(snap => {
        setLocalFavorites(new Set(snap.docs.map(d => d.data().productId)));
      }).catch(() => {});
    }, []);

    const filteredProducts = products.filter(product => {
      const matchesSearch = product.name.toLowerCase().includes(localSearch.toLowerCase());
      const matchesCategory = localCategory === 'all' || product.category === localCategory;
      return matchesSearch && matchesCategory;
    });

    return (
      <div className="space-y-6 pb-24">
        <div className="bg-white p-4 rounded-xl shadow-md">
          <h3 className="font-bold mb-1">💊 Catalogo Farmaci</h3>
          <p className="text-sm text-gray-500">Il rider acquisterà i prodotti nella farmacia più vicina a te</p>
        </div>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Cerca prodotti..."
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none"
            autoFocus
          />
        </div>

        <div className="flex gap-2 overflow-x-auto pb-2">
          {['all', 'Farmaci', 'Integratori', 'Cosmesi', 'Dispositivi'].map(cat => (
            <button
              key={cat}
              onClick={() => setLocalCategory(cat)}
              className={`px-4 py-2 rounded-full font-semibold whitespace-nowrap ${
                localCategory === cat
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {cat === 'all' ? 'Tutti' : cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-4">
          {filteredProducts.map(product => (
            <div key={product.id} className="bg-white rounded-xl shadow-md overflow-hidden">
              {product.imageUrl && (
                <div className="relative">
                  <img
                    src={product.imageUrl}
                    alt={product.name}
                    className="w-full h-36 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <button
                    onClick={async () => {
                      if (!currentUser?.uid) return;
                      const isFav = localFavorites.has(product.id);
                      if (isFav) {
                        // Rimuovi dai preferiti
                        try {
                          const snap = await getDocs(query(collection(db, 'users', currentUser.uid, 'favorites'), where('productId', '==', product.id)));
                          for (const d of snap.docs) await deleteDoc(doc(db, 'users', currentUser.uid, 'favorites', d.id));
                          setLocalFavorites(prev => { const n = new Set(prev); n.delete(product.id); return n; });
                        } catch(e) {}
                      } else {
                        // Aggiungi ai preferiti
                        try {
                          await addDoc(collection(db, 'users', currentUser.uid, 'favorites'), {
                            productId: product.id,
                            name: product.name,
                            description: product.description,
                            price: product.price,
                            imageUrl: product.imageUrl || '',
                            createdAt: serverTimestamp()
                          });
                          setLocalFavorites(prev => new Set([...prev, product.id]));
                        } catch(e) {}
                      }
                    }}
                    className="absolute top-2 right-2 w-9 h-9 rounded-full bg-white shadow-md flex items-center justify-center"
                  >
                    <Heart
                      size={18}
                      className={localFavorites.has(product.id) ? 'text-red-500 fill-red-500' : 'text-gray-400'}
                    />
                  </button>
                </div>
              )}
              <div className="p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="font-bold text-gray-800">{product.name}</h4>
                  <p className="text-sm text-gray-500">{product.description}</p>
                  {product.prescription && (
                    <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full border border-red-200">
                      📋 Ricetta obbligatoria
                    </span>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xl font-bold text-green-600">€{product.price.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">Stock: {product.stock}</p>
                </div>
              </div>
              <button
                onClick={() => addToCart(product)}
                disabled={product.stock === 0}
                className="w-full bg-green-600 text-white py-2 rounded-lg font-semibold hover:bg-green-700 transition disabled:bg-gray-400 flex items-center justify-center gap-2"
              >
                <Plus size={20} />
                Aggiungi al carrello
              </button>
            </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CartView = () => (
    <div className="space-y-6 pb-24">
      <h2 className="text-2xl font-bold">Carrello</h2>

      {cart.length === 0 ? (
        <div className="bg-white p-12 rounded-xl shadow-md text-center">
          <ShoppingCart className="mx-auto mb-4 text-gray-400" size={64} />
          <p className="text-gray-500 text-lg">Il carrello è vuoto</p>
          <button
            onClick={() => setCurrentView('products')}
            className="mt-4 bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition"
          >
            Inizia a fare acquisti
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {cart.map(item => (
              <div key={item.id} className="bg-white p-4 rounded-xl shadow-md">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-800">{item.name}</h4>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="text-red-500 hover:bg-red-50 p-2 rounded"
                  >
                    <X size={20} />
                  </button>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center"
                    >
                      <Minus size={16} />
                    </button>
                    <span className="font-bold text-lg w-8 text-center">{item.quantity}</span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-8 h-8 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center"
                    >
                      <Plus size={16} />
                    </button>
                  </div>
                  <span className="font-bold text-xl text-green-600">
                    €{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-6 rounded-xl shadow-md space-y-3">

            {/* AVVISO ORDINE PICCOLO */}
            {getCartTotal() < 10 && (
              <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg text-sm text-amber-800">
                ⚠️ Aggiungi <strong>€{(10 - getCartTotal()).toFixed(2)}</strong> per evitare il supplemento ordine piccolo
              </div>
            )}

            {/* BREAKDOWN COSTI */}
            <div className="flex justify-between text-gray-600">
              <span>Subtotale prodotti</span>
              <span>€{getCartTotal().toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-gray-600 text-sm">
              <span>Spese servizio (8%) ℹ️
                <span className="text-xs text-gray-400 block">min €0,79 — max €3,99</span>
              </span>
              <span>€{getServiceFee().toFixed(2)}</span>
            </div>
            {getSmallOrderSupplement() > 0 && (
              <div className="flex justify-between text-amber-600 text-sm">
                <span>Supplemento ordine piccolo ℹ️
                  <span className="text-xs text-amber-400 block">ordini sotto €10</span>
                </span>
                <span>€{getSmallOrderSupplement().toFixed(2)}</span>
              </div>
            )}
            {getSupplementoFestivo() > 0 && (
              <div className="flex justify-between text-amber-600 text-sm">
                <span>Supplemento festivo ℹ️
                  <span className="text-xs text-amber-400 block">sabato, domenica e festivi</span>
                </span>
                <span>€{getSupplementoFestivo().toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-gray-600">
              <span>Spese consegna</span>
              <span>€3.99</span>
            </div>

            {/* MANCIA RIDER */}
            <div className="border-t border-gray-100 pt-3">
              <p className="text-sm font-bold text-gray-700 mb-2">🛵 Mancia al Rider <span className="font-normal text-gray-400">(opzionale)</span></p>
              <div className="flex gap-2 mb-2">
                {[1, 2, 3, 5].map(amount => (
                  <button
                    key={amount}
                    onClick={() => { setMancia(amount); setManciaPersonalizzata(''); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold border-2 transition ${
                      mancia === amount 
                        ? 'bg-green-600 text-white border-green-600' 
                        : 'bg-white text-gray-600 border-gray-200 hover:border-green-400'
                    }`}
                  >
                    €{amount}
                  </button>
                ))}
                <button
                  onClick={() => setMancia(0)}
                  className={`flex-1 py-2 rounded-lg text-sm font-bold border-2 transition ${
                    mancia === 0 && !manciaPersonalizzata
                      ? 'bg-gray-200 text-gray-700 border-gray-300'
                      : 'bg-white text-gray-400 border-gray-200 hover:border-gray-400'
                  }`}
                >
                  Niente
                </button>
              </div>
              <input
                type="number"
                placeholder="Importo personalizzato..."
                value={manciaPersonalizzata}
                onChange={(e) => {
                  setManciaPersonalizzata(e.target.value);
                  setMancia(parseFloat(e.target.value) || 0);
                }}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:border-green-500"
                min="0"
                step="0.50"
              />
              {mancia > 0 && (
                <p className="text-xs text-green-600 mt-1">✅ Mancia di €{mancia.toFixed(2)} aggiunta</p>
              )}
            </div>

            {/* CODICE PROMO */}
            <div className="border-t border-gray-100 pt-3">
              <p className="text-sm font-bold text-gray-700 mb-2">🏷️ Hai un codice promo?</p>
              {promoApplicata ? (
                <div className="flex items-center justify-between bg-green-50 border border-green-200 p-3 rounded-xl">
                  <div>
                    <p className="font-black text-green-700">{promoApplicata.codice}</p>
                    <p className="text-xs text-green-600">
                      Sconto: -€{getScontoPromo().toFixed(2)}
                    </p>
                  </div>
                  <button onClick={() => { setPromoApplicata(null); setCodicePromo(''); }}
                    className="text-red-500 hover:text-red-700 font-bold text-sm">
                    Rimuovi
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={codicePromo}
                    onChange={e => setCodicePromo(e.target.value.toUpperCase())}
                    placeholder="Inserisci codice"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-green-500 uppercase font-bold"
                  />
                  <button
                    onClick={applicaCodicePromo}
                    disabled={promoLoading || !codicePromo.trim()}
                    className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-sm hover:bg-green-700 transition disabled:bg-gray-400"
                  >
                    {promoLoading ? '...' : 'Applica'}
                  </button>
                </div>
              )}
            </div>

            {/* TOTALE FINALE */}
            {promoApplicata && (
              <div className="flex justify-between text-sm text-green-600 font-bold">
                <span>🏷️ Sconto {promoApplicata.codice}</span>
                <span>-€{getScontoPromo().toFixed(2)}</span>
              </div>
            )}
            <div className="border-t-2 border-gray-200 pt-3 flex justify-between text-xl font-bold">
              <span>Totale</span>
              <span className="text-green-600">€{getFinalTotal().toFixed(2)}</span>
            </div>

            {!isServiceOpen() && (
              <div className="bg-orange-50 border-2 border-orange-200 p-4 rounded-xl mb-2">
                <p className="font-bold text-orange-800 mb-1">⏰ Siamo chiusi adesso</p>
                <p className="text-sm text-orange-600">Orari: 09:00-13:00 e 16:00-20:30 tutti i giorni</p>
                <p className="text-sm text-green-700 font-semibold mt-2 mb-3">
                  📅 Scegli una fascia oraria per il pre-ordine:
                </p>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {getSlotDisponibili().map((slot, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedSlot(slot)}
                      className={`py-2 px-3 rounded-xl text-xs font-bold border-2 transition ${
                        selectedSlot?.label === slot.label
                          ? 'bg-orange-500 text-white border-orange-500'
                          : 'bg-white text-orange-700 border-orange-200 hover:border-orange-400'
                      }`}
                    >
                      {slot.label.replace('Oggi ', '').replace('Domani ', '')}
                      {slot.label.startsWith('Domani') && <span className="block text-orange-400 font-normal">domani</span>}
                    </button>
                  ))}
                </div>
                {selectedSlot && (
                  <p className="text-xs text-green-700 font-semibold mt-2">
                    ✅ Selezionato: <strong>{selectedSlot.label}</strong>
                  </p>
                )}
              </div>
            )}

            <button
              onClick={() => {
                if (!isServiceOpen()) {
                  const slot = selectedSlot || getNextSlot();
                  if (!slot) return;
                  setSelectedSlot(slot);
                  setIsPreOrder(true);
                }
                setShowDeliveryForm(true);
              }}
              disabled={!isServiceOpen() && !selectedSlot && getSlotDisponibili().length === 0}
              className={`w-full py-4 rounded-xl font-bold text-lg transition shadow-lg text-white ${
                isServiceOpen()
                  ? 'bg-green-600 hover:bg-green-700'
                  : selectedSlot
                    ? 'bg-orange-500 hover:bg-orange-600'
                    : 'bg-orange-300 cursor-pointer hover:bg-orange-400'
              }`}
            >
              {isServiceOpen()
                ? 'Procedi al Checkout'
                : selectedSlot
                  ? `📅 Pre-ordina per ${selectedSlot.label}`
                  : `📅 Seleziona uno slot e pre-ordina`
              }
            </button>
          </div>
        </>
      )}
    </div>
  );

const TrackingView = () => {
  const [riderPosition, setRiderPosition] = useState(null);
  const [eta, setEta] = useState(null);

  useEffect(() => {
    if (currentOrder?.status !== 'delivering') return;

    const unsubscribe = onSnapshot(
      query(collection(db, 'rider-positions'), where('orderId', '==', currentOrder.id), where('isActive', '==', true)),
      (snapshot) => {
        if (!snapshot.empty) {
          const data = snapshot.docs[0].data();
          setRiderPosition({ lat: data.lat, lng: data.lng, riderName: data.riderName });

          // Stima ETA basata su distanza
          if (currentOrder.deliveryLat && currentOrder.deliveryLng) {
            const R = 6371;
            const dLat = (currentOrder.deliveryLat - data.lat) * Math.PI / 180;
            const dLon = (currentOrder.deliveryLng - data.lng) * Math.PI / 180;
            const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
                      Math.cos(data.lat * Math.PI / 180) * Math.cos(currentOrder.deliveryLat * Math.PI / 180) *
                      Math.sin(dLon/2) * Math.sin(dLon/2);
            const distance = R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
            const minutes = Math.round((distance / 30) * 60); // 30 km/h media
            setEta(Math.max(1, minutes));
          }
        } else {
          setRiderPosition(null);
        }
      }
    );

    return () => unsubscribe();
  }, [currentOrder?.status, currentOrder?.id]);



  const getStatusInfo = () => {
    switch (currentOrder?.status) {
      case 'scheduled':
        return { icon: Clock, color: 'orange', text: 'Pre-ordine', desc: `Consegna programmata per: ${currentOrder?.scheduledLabel}` };
      case 'preparing':
        return { icon: Package, color: 'blue', text: 'In preparazione', desc: 'Stiamo cercando un rider disponibile' };
      case 'ready':
        return { icon: CheckCircle, color: 'green', text: 'Pronto', desc: 'Il tuo ordine è pronto, un rider lo ritirerà a breve' };
      case 'accepted':
        return { icon: Truck, color: 'blue', text: 'Rider in arrivo', desc: 'Il rider sta andando in farmacia a ritirare il tuo ordine' };
      case 'delivering':
        return { icon: Truck, color: 'purple', text: 'In consegna', desc: 'Il rider sta consegnando il tuo ordine' };
      case 'in_revisione_admin':
        return { icon: Clock, color: 'orange', text: 'In verifica', desc: 'Alcuni prodotti potrebbero non essere disponibili. Stiamo verificando il tuo ordine e ti aggiorneremo a breve.' };
      case 'delivered':
        return { icon: CheckCircle, color: 'green', text: 'Consegnato', desc: 'Ordine consegnato con successo!' };
      case 'cancelled':
        return { icon: Package, color: 'red', text: 'Annullato', desc: 'L\'ordine è stato annullato. Se hai pagato con carta, il rimborso verrà accreditato entro 5-10 giorni lavorativi, in base alla tua banca.' };
      default:
        return { icon: Package, color: 'gray', text: 'In elaborazione', desc: 'Stiamo elaborando il tuo ordine' };
    }
  };

  const status = getStatusInfo();
  const StatusIcon = status.icon;

  const steps = currentOrder?.isPreOrder
    ? [
        { key: 'scheduled', label: 'Programmato', icon: '📅' },
        { key: 'preparing', label: 'Ricevuto', icon: '📋' },
        { key: 'ready', label: 'Pronto', icon: '✅' },
        { key: 'delivering', label: 'In consegna', icon: '🛵' },
        { key: 'delivered', label: 'Consegnato', icon: '🎉' },
      ]
    : [
        { key: 'preparing', label: 'Ricevuto', icon: '📋' },
        { key: 'ready', label: 'Pronto', icon: '✅' },
        { key: 'delivering', label: 'In consegna', icon: '🛵' },
        { key: 'delivered', label: 'Consegnato', icon: '🎉' },
      ];
  const stepIndex = steps.findIndex(s => s.key === currentOrder?.status);

  return (
    <div className="space-y-6 pb-24">
      {/* HEADER STATO */}
      <div className={`bg-gradient-to-r from-${status.color}-500 to-${status.color}-600 text-white p-6 rounded-xl shadow-lg`}>
        <div className="flex items-center gap-4 mb-4">
          <StatusIcon size={48} />
          <div>
            <h2 className="text-2xl font-bold">{status.text}</h2>
            <p className={`text-${status.color}-100`}>{status.desc}</p>
          </div>
        </div>
      </div>

      {/* PROGRESS BAR STATI */}
      <div className="bg-white p-4 rounded-xl shadow-md">
        <div className="flex items-center justify-between relative">
          {/* Linea di connessione */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200 z-0" />
          <div
            className="absolute top-5 left-0 h-1 bg-green-500 z-0 transition-all duration-500"
            style={{ width: `${stepIndex >= 0 ? (stepIndex / (steps.length - 1)) * 100 : 0}%` }}
          />
          {steps.map((step, idx) => {
            const isDone = idx <= stepIndex;
            const isCurrent = idx === stepIndex;
            return (
              <div key={step.key} className="flex flex-col items-center z-10">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 transition-all ${
                  isDone
                    ? 'bg-green-500 border-green-500 shadow-md'
                    : 'bg-white border-gray-300'
                } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                  {isDone ? (idx < stepIndex ? '✓' : step.icon) : step.icon}
                </div>
                <p className={`text-xs mt-2 font-semibold text-center ${isDone ? 'text-green-600' : 'text-gray-400'}`}>
                  {step.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>

{currentOrder?.status === 'delivering' && riderPosition && (
        <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-xl">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center">
              <Truck className="text-white" size={20} />
            </div>
            <div>
              <p className="font-bold text-purple-800">Rider in arrivo!</p>
              <p className="text-sm text-purple-600">{riderPosition.riderName}</p>
            </div>
            {eta && (
              <div className="ml-auto bg-purple-600 text-white px-3 py-2 rounded-xl text-center">
                <p className="text-xs">ETA</p>
                <p className="font-black text-lg">~{eta} min</p>
              </div>
            )}
          </div>
          <LeafletMapCliente
            riderPosition={riderPosition}
            deliveryLat={currentOrder?.deliveryLat}
            deliveryLng={currentOrder?.deliveryLng}
          />
          <div className="bg-white p-3 rounded-lg text-xs text-gray-500 flex items-center gap-2 mt-2">
            <MapPin size={14} className="text-purple-500" />
            <span>Posizione aggiornata in tempo reale ogni ~7 secondi</span>
          </div>
        </div>
      )}

      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-bold mb-4">Dettagli Ordine</h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">ID Ordine:</span>
            <span className="font-semibold">#{currentOrder?.id?.substring(0, 8)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Farmacia:</span>
            <span className="font-semibold">{currentOrder?.pharmacyName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Indirizzo:</span>
            <span className="font-semibold">{currentOrder?.deliveryAddress}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Totale:</span>
            <span className="font-semibold text-green-600">€{currentOrder?.total?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Pagamento:</span>
            <span className="font-semibold text-green-600">✓ Pagato</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-xl shadow-md">
        <h3 className="font-bold mb-4">Prodotti</h3>
        <div className="space-y-3">
          {currentOrder?.items?.map((item, idx) => (
            <div key={idx} className="flex justify-between items-center">
              <span className="text-gray-700">{item.quantity}x {item.name}</span>
              <span className="font-semibold">€{(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
      </div>

      {currentOrder?.status === 'delivering' && (
        <button
          onClick={() => setCurrentView('chat')}
          className="w-full bg-purple-600 text-white py-3 rounded-xl font-semibold hover:bg-purple-700 transition flex items-center justify-center gap-2"
        >
          <MessageCircle size={20} />
          💬 Messaggio al Rider
        </button>
      )}
      {currentOrder?.status !== 'delivering' && (
        <button
          onClick={() => setCurrentView('chat')}
          className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
        >
          <MessageCircle size={20} />
          Contatta supporto
        </button>
      )}

      {/* BOTTONE RECENSIONE */}
      {currentOrder?.status === 'delivered' && currentOrder?.riderId && 
        !localStorage.getItem(`reviewed_${currentOrder?.id}`) && (
        <button
          onClick={() => setShowReviewModal(true)}
          className="w-full bg-yellow-400 text-yellow-900 py-3 rounded-xl font-bold hover:bg-yellow-500 transition flex items-center justify-center gap-2"
        >
          ⭐ Valuta il Rider
        </button>
      )}
      {/* SEGNALA PROBLEMA */}
      {currentOrder?.status === 'delivered' && !localStorage.getItem(`problema_${currentOrder?.id}`) && (
        <button
          onClick={() => setShowProblemaModal(true)}
          className="w-full bg-red-50 border-2 border-red-200 text-red-600 py-3 rounded-xl font-bold hover:bg-red-100 transition flex items-center justify-center gap-2"
        >
          ⚠️ Segnala un problema
        </button>
      )}
    </div>
  );
};

const OrdersView = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [richiesteRicetta, setRichiesteRicetta] = useState([]);
  const [tab, setTab] = useState('ordini'); // 'ordini' | 'ricette'

  useEffect(() => {
    if (!currentUser?.uid) return;
    const q = query(
      collection(db, 'orders'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAllOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    if (!currentUser?.uid) return;
    // Query senza orderBy per NON richiedere un indice composto (userId + createdAt).
    // L'ordinamento per data si fa lato codice sui risultati ricevuti.
    const q = query(
      collection(db, 'richieste-ricetta'),
      where('userId', '==', currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const richieste = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          const da = a.createdAt?.toMillis ? a.createdAt.toMillis() : new Date(a.createdAt || 0).getTime();
          const db_ = b.createdAt?.toMillis ? b.createdAt.toMillis() : new Date(b.createdAt || 0).getTime();
          return db_ - da;
        });
      setRichiesteRicetta(richieste);
    });
    return () => unsubscribe();
  }, [currentUser?.uid]);

  const statoRicetta = (status) => {
    if (status === 'pending') return { label: '⏳ In attesa', color: 'bg-yellow-100 text-yellow-700' };
    if (status === 'in_progress') return { label: '🔄 In lavorazione', color: 'bg-blue-100 text-blue-700' };
    if (status === 'completed') return { label: '🛵 Rider in arrivo', color: 'bg-purple-100 text-purple-700' };
    if (status === 'rejected') return { label: '❌ Rifiutata', color: 'bg-red-100 text-red-700' };
    return { label: status, color: 'bg-gray-100 text-gray-700' };
  };

  return (
    <div className="space-y-4 pb-24">
      <h2 className="text-2xl font-bold">I miei ordini</h2>

      {/* TAB */}
      <div className="flex rounded-xl bg-gray-100 p-1">
        <button
          onClick={() => setTab('ordini')}
          className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
            tab === 'ordini' ? 'bg-white text-green-600 shadow' : 'text-gray-500'
          }`}
        >
          📦 Ordini ({allOrders.length})
        </button>
        <button
          onClick={() => setTab('ricette')}
          className={`flex-1 py-2 rounded-lg font-semibold text-sm transition ${
            tab === 'ricette' ? 'bg-white text-red-600 shadow' : 'text-gray-500'
          }`}
        >
          📋 Ricette ({richiesteRicetta.length})
        </button>
      </div>

      {/* ORDINI NORMALI */}
      {tab === 'ordini' && (
        <>
          {allOrders.length === 0 ? (
            <div className="bg-white p-12 rounded-xl text-center">
              <Package className="mx-auto mb-4 text-gray-400" size={64} />
              <p className="text-gray-500">Nessun ordine effettuato</p>
            </div>
          ) : (
            allOrders.map(order => (
              <div key={order.id} className="bg-white p-4 rounded-xl shadow-md cursor-pointer"
                onClick={() => { setCurrentOrder(order); setCurrentView('tracking'); }}>
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-bold">#{order.id.substring(0, 8)}</p>
                    <p className="text-sm text-gray-500">{order.pharmacyName}</p>
                    {order.isRicettaOrder && (
                      <span className="text-xs bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">📋 Con Ricetta</span>
                    )}
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                    order.status === 'delivered' ? 'bg-green-100 text-green-700' :
                    order.status === 'delivering' ? 'bg-purple-100 text-purple-700' :
                    order.status === 'accepted' ? 'bg-blue-100 text-blue-700' :
                    order.status === 'ready' ? 'bg-blue-100 text-blue-700' :
                    order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-orange-100 text-orange-700'
                  }`}>
                    {order.status === 'delivered' ? 'Consegnato' :
                     order.status === 'delivering' ? 'In consegna' :
                     order.status === 'accepted' ? 'Rider in arrivo' :
                     order.status === 'ready' ? 'Pronto' :
                     order.status === 'cancelled' ? 'Annullato' :
                     'In preparazione'}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">{new Date(order.createdAt).toLocaleDateString('it-IT')}</span>
                  <span className="font-bold text-green-600">€{order.total?.toFixed(2)}</span>
                </div>
              </div>
            ))
          )}
        </>
      )}

      {/* RICHIESTE RICETTA */}
      {tab === 'ricette' && (
        <>
          {richiesteRicetta.length === 0 ? (
            <div className="bg-white p-12 rounded-xl text-center">
              <div className="text-5xl mb-4">📋</div>
              <p className="text-gray-500">Nessuna richiesta ricetta inviata</p>
              <button
                onClick={() => setShowRicettaRequest(true)}
                className="mt-4 bg-red-600 text-white px-6 py-3 rounded-xl font-bold"
              >
                📋 Ordina con Ricetta
              </button>
            </div>
          ) : (
            richiesteRicetta.map(r => {
              const stato = statoRicetta(r.status);
              return (
                <div key={r.id} className="bg-white p-4 rounded-xl shadow-md">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-bold text-gray-900">Richiesta Ricetta</p>
                      <p className="text-xs text-gray-400">#{r.id.substring(0, 8)}</p>
                      <p className="text-xs text-gray-500 mt-1">{new Date(r.createdAt).toLocaleDateString('it-IT')}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${stato.color}`}>
                      {stato.label}
                    </span>
                  </div>

                  {r.note && (
                    <div className="bg-gray-50 rounded-lg p-2 mb-3">
                      <p className="text-xs text-gray-500">Note: {r.note}</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Servizio pagato:</span>
                    <span className="font-bold text-green-600">€{(r.servizioPagato || 4.99).toFixed(2)}</span>
                  </div>

                  {r.status === 'rejected' && (
                    <div className="mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-xs text-red-700 font-semibold">La tua richiesta è stata rifiutata. Contatta il supporto per informazioni.</p>
                    </div>
                  )}

                  {r.status === 'pending' && (
                    <div className="mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-xs text-yellow-700">⏳ Il nostro team sta esaminando la tua ricetta. Ti aggiorneremo presto.</p>
                    </div>
                  )}

                  {r.status === 'in_progress' && (
                    <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-xs text-blue-700">🔄 La tua ricetta è in lavorazione. Un rider verrà assegnato a breve.</p>
                    </div>
                  )}

                  {r.status === 'completed' && (
                    <div className="mt-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <p className="text-xs text-purple-700">🛵 Un rider è stato assegnato e si sta recando in farmacia!</p>
                    </div>
                  )}

                  <button
                    onClick={() => window.open(r.ricettaUrl, '_blank')}
                    className="mt-3 w-full bg-gray-50 border border-gray-200 text-gray-600 py-2 rounded-lg font-semibold text-sm hover:bg-gray-100 transition"
                  >
                    👁 Vedi Ricetta
                  </button>
                </div>
              );
            })
          )}
        </>
      )}
    </div>
  );
};

const ChatView = () => {
  const [realtimeMessages, setRealtimeMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const messagesEndRef = React.useRef(null);

  const quickMessages = [
    'Suona al citofono 🔔',
    'Sono al primo piano 🏠',
    'Lascia alla porta 📦',
    'Arrivo subito! ⏱️',
    'Grazie mille! 🙏'
  ];

  useEffect(() => {
    if (!currentOrder?.id) return;
    const q = query(
      collection(db, 'orders', currentOrder.id, 'chat'),
      orderBy('createdAt', 'asc')
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Notifica per nuovi messaggi dal rider
      if (msgs.length > realtimeMessages.length) {
        const lastMsg = msgs[msgs.length - 1];
        if (lastMsg.sender === 'rider') {
          // Notifica browser
          if (Notification.permission === 'granted') {
            new Notification('Messaggio dal Rider 🛵', {
              body: lastMsg.text,
              icon: '/logo192.png'
            });
          }
          // Vibrazione se mobile
          if (navigator.vibrate) navigator.vibrate([200, 100, 200]);
        }
      }
      
      setRealtimeMessages(msgs);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    });
    return () => unsubscribe();
  }, [currentOrder?.id, realtimeMessages.length]);

  const sendChatMessage = async (text) => {
    const msg = text || chatInput.trim();
    if (!msg || !currentOrder?.id) return;
    setChatInput('');
    try {
      await addDoc(collection(db, 'orders', currentOrder.id, 'chat'), {
        text: msg,
        sender: 'cliente',
        senderName: currentOrder.customerName || 'Cliente',
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Errore invio messaggio:', error);
    }
  };

  if (currentOrder?.status !== 'delivering') {
    return (
      <div className="flex flex-col h-screen pb-24">
        <div className="bg-white border-b border-gray-200 p-4 flex items-center gap-3">
          <button onClick={() => setCurrentView('tracking')} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
          <h2 className="font-bold text-lg">Chat con Rider</h2>
        </div>
        <div className="flex-1 flex items-center justify-center p-8 text-center text-gray-500">
          <div>
            <MessageCircle size={48} className="mx-auto mb-4 opacity-30" />
            <p className="font-semibold">Chat disponibile solo durante la consegna</p>
            <p className="text-sm mt-2">Quando il rider è in consegna potrai chattare con lui</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen pb-24">
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setCurrentView('tracking')} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={20} />
            </button>
            <div>
              <h2 className="font-bold text-lg">Chat con Rider</h2>
              <p className="text-xs text-green-600">🟢 {currentOrder.riderName || 'Rider'} in consegna</p>
            </div>
          </div>
          {currentOrder.riderPhone && (
            
             <a href={`tel:${currentOrder.riderPhone}`}
              className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-xl font-semibold text-sm"
            >
              <Phone size={16} />
              Chiama
            </a>
          )}
        </div>
      </div>

      {/* MESSAGGI RAPIDI */}
      <div className="bg-gray-50 p-3 border-b border-gray-200">
        <p className="text-xs text-gray-500 mb-2">Messaggi rapidi:</p>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {quickMessages.map((msg, idx) => (
            <button
              key={idx}
              onClick={() => sendChatMessage(msg)}
              className="flex-shrink-0 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-full text-xs font-semibold hover:border-green-500 hover:text-green-600 transition"
            >
              {msg}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {realtimeMessages.length === 0 && (
          <div className="text-center text-gray-400 py-8">
            <MessageCircle size={32} className="mx-auto mb-2 opacity-30" />
            <p className="text-sm">Nessun messaggio ancora. Scrivi al rider!</p>
          </div>
        )}
        {realtimeMessages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender === 'cliente' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] ${
              msg.sender === 'cliente'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-800 border border-gray-200'
            } p-3 rounded-2xl shadow-sm`}>
              <p className="text-sm">{msg.text}</p>
              <p className={`text-xs mt-1 ${msg.sender === 'cliente' ? 'text-green-100' : 'text-gray-400'}`}>
                {msg.createdAt?.toDate ? msg.createdAt.toDate().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' }) : '...'}
              </p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
            placeholder="Scrivi un messaggio al rider..."
            className="flex-1 px-4 py-3 rounded-xl bg-gray-100 border-0 focus:ring-2 focus:ring-green-500 outline-none text-sm"
          />
          <button
            onClick={() => sendChatMessage()}
            className="bg-green-600 text-white px-5 py-3 rounded-xl font-semibold hover:bg-green-700 transition"
          >
            Invia
          </button>
        </div>
      </div>
    </div>
  );
};

// PROFILE VIEW
  const ProfileView = () => {
    const [profileSection, setProfileSection] = React.useState(null);
    const uploadRicetta = async (file) => {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) { alert('⚠️ File troppo grande. Massimo 10MB.'); return; }
      setUploadingRicetta(true);
      try {
        const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
        const storage = getStorage();
        const ext = file.name.split('.').pop();
        const storageRef = ref(storage, `ricette/${currentUser.uid}/${Date.now()}.${ext}`);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        await addDoc(collection(db, 'users', currentUser.uid, 'ricette'), {
          url,
          nome: file.name,
          tipo: file.type,
          createdAt: new Date().toISOString()
        });
        alert('✅ Ricetta caricata con successo!');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setUploadingRicetta(false);
      }
    };

    const eliminaRicetta = async (ricetta) => {
      if (!window.confirm('Eliminare questa ricetta?')) return;
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'ricette', ricetta.id));
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };


    const handleRicettaClick = () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/heic,image/heif,image/*,application/pdf';
      input.style.position = 'fixed';
      input.style.top = '-100px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.addEventListener('change', (e) => {
        const file = e.target.files[0];
        document.body.removeChild(input);
        if (file) uploadRicetta(file);
      });
      input.addEventListener('cancel', () => document.body.removeChild(input));
      input.click();
    };
    const [userProfile, setUserProfile] = React.useState({ nome: '', cognome: '', telefono: '' });
    const [savedAddresses, setSavedAddresses] = React.useState([]);
    const [favorites, setFavorites] = React.useState([]);
    const [notifEmail, setNotifEmail] = React.useState(true);
    const [notifPush, setNotifPush] = React.useState(true);
    const [showLangPopup, setShowLangPopup] = React.useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
    const [editingAddress, setEditingAddress] = React.useState(null);
    const [newAddress, setNewAddress] = React.useState({ label: '', address: '', city: '', zipCode: '' });
    const [showAddAddress, setShowAddAddress] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
      if (!currentUser?.uid) return;
      // Carica profilo utente
      getDoc(doc(db, 'users', currentUser.uid)).then(snap => {
        if (snap.exists()) setUserProfile(snap.data());
      }).catch(() => {});
      // Carica indirizzi
      getDocs(query(collection(db, 'users', currentUser.uid, 'addresses'))).then(snap => {
        setSavedAddresses(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }).catch(() => {});
      // Carica preferiti
      getDocs(query(collection(db, 'users', currentUser.uid, 'favorites'))).then(snap => {
        setFavorites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }).catch(() => {});
    }, []);

    const saveProfile = async () => {
      setSaving(true);
      try {
        await setDoc(doc(db, 'users', currentUser.uid), {
          ...userProfile,
          email: currentUser.email,
          updatedAt: serverTimestamp()
        }, { merge: true });
        alert('✅ Profilo aggiornato!');
        setProfileSection(null);
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setSaving(false);
      }
    };

    const saveAddress = async () => {
      if (!newAddress.label || !newAddress.address || !newAddress.city) {
        alert('Compila tutti i campi');
        return;
      }
      try {
        const ref = await addDoc(collection(db, 'users', currentUser.uid, 'addresses'), {
          ...newAddress,
          createdAt: serverTimestamp()
        });
        setSavedAddresses([...savedAddresses, { id: ref.id, ...newAddress }]);
        setNewAddress({ label: '', address: '', city: '', zipCode: '' });
        setShowAddAddress(false);
        alert('✅ Indirizzo salvato!');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const deleteAddress = async (id) => {
      if (!window.confirm('Eliminare questo indirizzo?')) return;
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'addresses', id));
        setSavedAddresses(savedAddresses.filter(a => a.id !== id));
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      }
    };

    const removeFavorite = async (id) => {
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid, 'favorites', id));
        setFavorites(favorites.filter(f => f.id !== id));
      } catch (e) {}
    };

    const deleteAccount = async () => {
      try {
        await deleteDoc(doc(db, 'users', currentUser.uid));
        await auth.currentUser.delete();
        handleLogout();
      } catch (e) {
        alert('❌ Errore: rieffettua il login e riprova');
      }
    };

    // Popup lingua
    if (showLangPopup) return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
          <h3 className="text-xl font-black text-gray-900 mb-2">🌍 Lingua App</h3>
          <p className="text-gray-500 text-sm mb-4">Imposta la tua lingua preferita. Puoi modificare la lingua dell'app dalle impostazioni del dispositivo.</p>
          <div className="space-y-2 mb-6">
            {[{ flag: '🇮🇹', name: 'Italiano', active: true }, { flag: '🇬🇧', name: 'English', active: false }, { flag: '🇫🇷', name: 'Français', active: false }].map(l => (
              <div key={l.name} className={`flex items-center justify-between p-3 rounded-xl border-2 ${l.active ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                <span>{l.flag} {l.name}</span>
                {l.active && <CheckCircle className="text-green-600" size={20} />}
              </div>
            ))}
          </div>
          <button onClick={() => setShowLangPopup(false)} className="w-full bg-green-600 text-white py-3 rounded-xl font-bold">Chiudi</button>
        </div>
      </div>
    );

    // Sezione Gestisci Account
    if (profileSection === 'account') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Gestisci Account</h2>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-md space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Nome</label>
            <input type="text" value={userProfile.nome || ''} onChange={e => setUserProfile({...userProfile, nome: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="Mario" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Cognome</label>
            <input type="text" value={userProfile.cognome || ''} onChange={e => setUserProfile({...userProfile, cognome: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="Rossi" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Telefono</label>
            <input type="tel" value={userProfile.telefono || ''} onChange={e => setUserProfile({...userProfile, telefono: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none" placeholder="+39 333 1234567" />
          </div>
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
            <input type="email" value={currentUser?.email || ''} disabled
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-100 bg-gray-50 text-gray-400 outline-none" />
            <p className="text-xs text-gray-400 mt-1">L'email non può essere modificata</p>
          </div>
          <button onClick={saveProfile} disabled={saving}
            className="w-full bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400">
            {saving ? 'Salvataggio...' : '✅ Salva Modifiche'}
          </button>
        </div>
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <button onClick={handleLogout} className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 transition border-b border-gray-100">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <LogOut className="text-orange-600" size={20} />
            </div>
            <span className="font-semibold text-gray-800">Esci dall'account</span>
          </button>
          <button onClick={() => setShowDeleteConfirm(true)} className="w-full flex items-center gap-3 p-4 hover:bg-red-50 transition">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <Trash className="text-red-600" size={20} />
            </div>
            <span className="font-semibold text-red-600">Elimina Account</span>
          </button>
        </div>
        {showDeleteConfirm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
              <h3 className="text-xl font-black text-gray-900 mb-2">⚠️ Elimina Account</h3>
              <p className="text-gray-500 text-sm mb-6">Questa azione è irreversibile. Tutti i tuoi dati verranno eliminati permanentemente.</p>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-xl font-bold">Annulla</button>
                <button onClick={deleteAccount} className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold">Elimina</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    // Sezione Preferiti
    if (profileSection === 'favorites') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Preferiti</h2>
        </div>
        {favorites.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <Heart className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="text-gray-500 font-semibold">Nessun preferito ancora</p>
            <p className="text-sm text-gray-400 mt-1">Aggiungi prodotti ai preferiti dal catalogo</p>
            <button onClick={() => { setProfileSection(null); setCurrentView('products'); }}
              className="mt-4 bg-green-600 text-white px-6 py-2 rounded-xl font-semibold">
              Sfoglia Catalogo
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {favorites.map(f => (
              <div key={f.id} className="bg-white p-4 rounded-2xl shadow-md flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900">{f.name}</p>
                  <p className="text-sm text-gray-500">{f.description}</p>
                  <p className="text-green-600 font-bold">€{Number(f.price).toFixed(2)}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { addToCart(f); setCurrentView('cart'); }}
                    className="bg-green-600 text-white px-3 py-2 rounded-xl text-sm font-bold">
                    + Carrello
                  </button>
                  <button onClick={() => removeFavorite(f.id)}
                    className="p-2 bg-red-100 rounded-xl">
                    <Trash className="text-red-500" size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );

    // Sezione Indirizzi
    if (profileSection === 'addresses') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
              <ChevronRight className="rotate-180 text-gray-600" size={24} />
            </button>
            <h2 className="text-xl font-black text-gray-900">Indirizzi Salvati</h2>
          </div>
          <button onClick={() => setShowAddAddress(true)} className="bg-green-600 text-white px-4 py-2 rounded-xl font-bold text-sm">
            + Aggiungi
          </button>
        </div>
        {savedAddresses.length === 0 && !showAddAddress ? (
          <div className="bg-white rounded-2xl p-12 shadow-md text-center">
            <MapPin className="mx-auto mb-4 text-gray-300" size={48} />
            <p className="text-gray-500 font-semibold">Nessun indirizzo salvato</p>
            <button onClick={() => setShowAddAddress(true)} className="mt-4 bg-green-600 text-white px-6 py-2 rounded-xl font-semibold">
              Aggiungi Indirizzo
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {savedAddresses.map(a => (
              <div key={a.id} className="bg-white p-4 rounded-2xl shadow-md flex items-center justify-between">
                <div>
                  <p className="font-bold text-gray-900">{a.label}</p>
                  <p className="text-sm text-gray-500">{a.address}, {a.city} {a.zipCode}</p>
                </div>
                <button onClick={() => deleteAddress(a.id)} className="p-2 bg-red-100 rounded-xl">
                  <Trash className="text-red-500" size={18} />
                </button>
              </div>
            ))}
          </div>
        )}
        {showAddAddress && (
          <div className="bg-white rounded-2xl p-6 shadow-md space-y-3">
            <h3 className="font-black text-gray-900">Nuovo Indirizzo</h3>
            <input type="text" value={newAddress.label} onChange={e => setNewAddress({...newAddress, label: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none"
              placeholder="Es. Casa, Ufficio..." />
            <input type="text" value={newAddress.address} onChange={e => setNewAddress({...newAddress, address: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none"
              placeholder="Via e numero civico" />
            <div className="grid grid-cols-2 gap-3">
              <input type="text" value={newAddress.city} onChange={e => setNewAddress({...newAddress, city: e.target.value})}
                className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none"
                placeholder="Città" />
              <input type="text" value={newAddress.zipCode} onChange={e => setNewAddress({...newAddress, zipCode: e.target.value})}
                className="px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-green-500 outline-none"
                placeholder="CAP" />
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowAddAddress(false)} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-xl font-bold">Annulla</button>
              <button onClick={saveAddress} className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold">Salva</button>
            </div>
          </div>
        )}
      </div>
    );

    // Sezione Metodo Pagamento
    if (profileSection === 'ricette') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronLeft size={22} className="text-gray-600" />
          </button>
          <h2 className="text-xl font-black text-gray-900">Le mie Ricette</h2>
        </div>

        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
          <p className="text-sm text-yellow-800 font-semibold">📋 Come funziona</p>
          <p className="text-xs text-yellow-700 mt-1">Carica qui le tue ricette mediche. Quando ordini prodotti con ricetta obbligatoria, potrai selezionare la ricetta al momento del checkout.</p>
        </div>

        <button onClick={handleRicettaClick} disabled={uploadingRicetta}
          className="w-full bg-red-600 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 disabled:bg-gray-400">
          {uploadingRicetta ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Caricamento...</> : '📋 Carica Nuova Ricetta'}
        </button>

        {ricette.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <div className="text-5xl mb-3">📋</div>
            <p className="font-semibold">Nessuna ricetta caricata</p>
            <p className="text-sm mt-1">Carica le tue ricette mediche per ordini più veloci</p>
          </div>
        ) : (
          <div className="space-y-3">
            {ricette.map(r => (
              <div key={r.id} className="bg-white rounded-2xl shadow-md p-4 flex items-center gap-4">
                <div className="w-14 h-14 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl">
                  {r.tipo === 'application/pdf' ? '📄' : '🖼️'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 truncate">{r.nome}</p>
                  <p className="text-xs text-gray-500">{new Date(r.createdAt).toLocaleDateString('it-IT')}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => window.open(r.url, '_blank')}
                    className="bg-green-50 border border-green-200 text-green-700 text-xs font-bold px-3 py-2 rounded-lg">
                    👁 Vedi
                  </button>
                  <button onClick={() => eliminaRicetta(r)}
                    className="bg-red-50 border border-red-200 text-red-700 text-xs font-bold px-3 py-2 rounded-lg">
                    🗑
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
    if (profileSection === 'payment') {
      const PaymentSection = () => {
        const [cards, setCards] = React.useState([]);
        const [loadingCards, setLoadingCards] = React.useState(true);
        const [showAddCard, setShowAddCard] = React.useState(false);
        const [customerId, setCustomerId] = React.useState(null);
        const [clientSecret, setClientSecret] = React.useState(null);
        const [loadingSetup, setLoadingSetup] = React.useState(false);
        const [deletingId, setDeletingId] = React.useState(null);

        React.useEffect(() => {
          initPayment();
        }, []);

        const initPayment = async () => {
          setLoadingCards(true);
          try {
            // Crea o recupera Stripe customer
            const custRes = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/createOrGetStripeCustomer', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ userId: currentUser.uid, email: currentUser.email })
            });
            const custData = await custRes.json();
            if (!custRes.ok) throw new Error(custData.error);
            setCustomerId(custData.customerId);

            // Carica carte salvate
            const pmRes = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/getPaymentMethods', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ customerId: custData.customerId })
            });
            const pmData = await pmRes.json();
            setCards(pmData.paymentMethods || []);
          } catch (e) {
            console.error('Errore init payment:', e);
          } finally {
            setLoadingCards(false);
          }
        };

        const handleAddCard = async () => {
          setLoadingSetup(true);
          try {
            const res = await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/createSetupIntent', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ customerId })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.error);
            setClientSecret(data.clientSecret);
            setShowAddCard(true);
          } catch (e) {
            alert('❌ Errore: ' + e.message);
          } finally {
            setLoadingSetup(false);
          }
        };

        const handleDeleteCard = async (pmId) => {
          if (!window.confirm('Rimuovere questa carta?')) return;
          setDeletingId(pmId);
          try {
            await fetch('https://us-central1-farmacia-subito.cloudfunctions.net/deletePaymentMethod', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ paymentMethodId: pmId })
            });
            setCards(cards.filter(c => c.id !== pmId));
          } catch (e) {
            alert('❌ Errore rimozione carta');
          } finally {
            setDeletingId(null);
          }
        };

        const brandIcon = (brand) => {
          const icons = { visa: '💳 Visa', mastercard: '💳 Mastercard', amex: '💳 Amex', discover: '💳 Discover' };
          return icons[brand] || '💳 Carta';
        };

        if (showAddCard && clientSecret) {
          return (
            <div className="space-y-4 pb-24">
              <div className="flex items-center gap-3 mb-2">
                <button onClick={() => { setShowAddCard(false); setClientSecret(null); }} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ChevronRight className="rotate-180 text-gray-600" size={24} />
                </button>
                <h2 className="text-xl font-black text-gray-900">Aggiungi Carta</h2>
              </div>
              <div className="bg-white rounded-2xl p-6 shadow-md">
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <SetupCardForm
                    clientSecret={clientSecret}
                    onSuccess={() => { setShowAddCard(false); setClientSecret(null); initPayment(); }}
                    onCancel={() => { setShowAddCard(false); setClientSecret(null); }}
                  />
                </Elements>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-4 pb-24">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <ChevronRight className="rotate-180 text-gray-600" size={24} />
                </button>
                <h2 className="text-xl font-black text-gray-900">Metodo di Pagamento</h2>
              </div>
            </div>

            {loadingCards ? (
              <div className="bg-white rounded-2xl p-8 shadow-md text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Caricamento...</p>
              </div>
            ) : cards.length === 0 ? (
              <div className="bg-white rounded-2xl p-8 shadow-md text-center">
                <CreditCard className="mx-auto mb-4 text-gray-300" size={48} />
                <p className="text-gray-700 font-bold mb-1">Nessuna carta salvata</p>
                <p className="text-sm text-gray-400 mb-6">Aggiungi una carta per pagare più velocemente</p>
                <button onClick={handleAddCard} disabled={loadingSetup}
                  className="bg-green-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-700 transition disabled:bg-gray-400">
                  {loadingSetup ? 'Caricamento...' : '+ Aggiungi Carta'}
                </button>
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {cards.map(card => (
                    <div key={card.id} className="bg-white rounded-2xl p-4 shadow-md flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-xl">
                          💳
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{brandIcon(card.card.brand)} •••• {card.card.last4}</p>
                          <p className="text-xs text-gray-500">Scade {card.card.exp_month}/{card.card.exp_year}</p>
                        </div>
                      </div>
                      <button onClick={() => handleDeleteCard(card.id)} disabled={deletingId === card.id}
                        className="p-2 bg-red-100 hover:bg-red-200 rounded-xl transition">
                        <Trash className="text-red-500" size={18} />
                      </button>
                    </div>
                  ))}
                </div>
                <button onClick={handleAddCard} disabled={loadingSetup}
                  className="w-full border-2 border-dashed border-green-400 text-green-600 py-4 rounded-2xl font-bold hover:bg-green-50 transition disabled:opacity-50">
                  {loadingSetup ? 'Caricamento...' : '+ Aggiungi un\'altra carta'}
                </button>
              </>
            )}

            <div className="bg-gray-50 rounded-2xl p-4 text-center">
              <p className="text-xs text-gray-400">🔒 I dati della carta sono protetti da Stripe — non vengono mai salvati sui nostri server</p>
            </div>
          </div>
        );
      };
      return <PaymentSection />;
    }

    // Sezione Aiuto
    if (profileSection === 'help') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Aiuto</h2>
        </div>
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <button onClick={() => setCurrentView('chat')}
            className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition border-b border-gray-100">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <MessageCircle className="text-green-600" size={24} />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900">Chat con il Supporto</p>
              <p className="text-sm text-gray-500">Parla con il nostro team</p>
            </div>
            <ChevronRight className="text-gray-400 ml-auto" size={20} />
          </button>
          <button onClick={() => setCurrentView('orders')}
            className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Package className="text-blue-600" size={24} />
            </div>
            <div className="text-left">
              <p className="font-bold text-gray-900">Assistenza su un Ordine</p>
              <p className="text-sm text-gray-500">Problemi con una consegna?</p>
            </div>
            <ChevronRight className="text-gray-400 ml-auto" size={20} />
          </button>
        </div>
        <div className="bg-white rounded-2xl p-4 shadow-md">
          <p className="text-sm text-gray-600 text-center">
            Puoi anche scriverci a{' '}
            <a href="mailto:info@farmaciasubito.it" className="text-green-600 font-bold">info@farmaciasubito.it</a>
          </p>
        </div>
      </div>
    );

    // Sezione Preferenze Contatto
    if (profileSection === 'notifications') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Preferenze di Contatto</h2>
        </div>
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <Bell className="text-blue-600" size={20} />
              </div>
              <div>
                <p className="font-bold text-gray-900">Notifiche Push</p>
                <p className="text-xs text-gray-500">
                  {permessoNotifiche === 'granted'
                    ? 'Attive — aggiornamenti sugli ordini'
                    : permessoNotifiche === 'denied'
                      ? 'Bloccate dal dispositivo'
                      : 'Tocca per attivare gli aggiornamenti'}
                </p>
              </div>
            </div>
            {permessoNotifiche === 'denied' ? (
              <span className="text-[10px] text-gray-400 text-right max-w-[100px] leading-tight">
                Attivale dalle impostazioni del telefono
              </span>
            ) : (
              <button
                onClick={permessoNotifiche === 'granted' ? undefined : attivaNotifiche}
                disabled={attivandoNotifiche || permessoNotifiche === 'granted'}
                className={`w-12 h-6 rounded-full transition relative flex-shrink-0 ${
                  permessoNotifiche === 'granted' ? 'bg-green-500' : 'bg-gray-300'
                } ${attivandoNotifiche ? 'opacity-50' : ''}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${
                  permessoNotifiche === 'granted' ? 'right-1' : 'left-1'
                }`} />
              </button>
            )}
          </div>
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Bell className="text-purple-600" size={20} />
              </div>
              <div>
                <p className="font-bold text-gray-900">Email</p>
                <p className="text-xs text-gray-500">Ricevi email per ordini e offerte</p>
              </div>
            </div>
            <button onClick={() => setNotifEmail(!notifEmail)}
              className={`w-12 h-6 rounded-full transition ${notifEmail ? 'bg-green-500' : 'bg-gray-300'} relative`}>
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${notifEmail ? 'right-1' : 'left-1'}`} />
            </button>
          </div>
        </div>
      </div>
    );

    // Sezione FAQ
    if (profileSection === 'faq') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">FAQ</h2>
        </div>
        <div className="space-y-3">
          {[
            { q: 'Come funziona FarmaciaSubito?', a: 'Scegli i prodotti dal catalogo, inserisci il tuo indirizzo e un rider si recherà nella farmacia più vicina a te per acquistarli e consegnarli a domicilio.' },
            { q: 'Quanto tempo ci vuole per la consegna?', a: 'Di solito 20-40 minuti, dipende dalla distanza tra la farmacia e il tuo indirizzo.' },
            { q: 'Come posso pagare?', a: 'Puoi pagare con carta di credito/debito (Visa, Mastercard, Amex) oppure in contanti alla consegna, disponibile dopo aver completato il primo ordine pagato con carta.' },
            { q: 'Posso ordinare farmaci con ricetta?', a: 'Sì. Dalla sezione "Con ricetta" carichi la foto della tua ricetta medica: il servizio di reperimento e consegna costa €4,99, addebitati sulla carta al momento della richiesta. Il rider acquista il farmaco in farmacia per tuo conto e te lo consegna a casa; il costo del farmaco lo paghi in contanti al rider alla consegna, insieme allo scontrino della farmacia.' },
            { q: 'Cosa succede se il prodotto non è disponibile?', a: 'Il rider ti contatterà tramite chat per aggiornarti. Potrai scegliere un\'alternativa o annullare l\'ordine.' },
            { q: 'Come posso tracciare il mio ordine?', a: 'Dalla sezione Ordini puoi vedere lo stato in tempo reale e seguire il rider sulla mappa durante la consegna.' },
            { q: 'Quali sono i costi del servizio?', a: 'Oltre al prezzo dei prodotti: spese di consegna €3,99; costo di servizio pari all\'8% dei prodotti (minimo €0,79, massimo €3,99); supplemento di €1,00 per gli ordini di sabato, domenica e nei giorni festivi; supplemento per ordini sotto €10 (fino a €2,00). Tutte le voci sono sempre indicate nel dettaglio prima della conferma dell\'ordine.' },
            { q: 'Posso annullare un ordine?', a: 'Puoi annullare l\'ordine entro pochi minuti dall\'invio. Contatta il supporto tramite chat.' },
          ].map((item, i) => (
            <FaqItem key={i} question={item.q} answer={item.a} />
          ))}
        </div>
      </div>
    );

    // Sezione Info
    if (profileSection === 'info') return (
      <div className="space-y-4 pb-24">
        <div className="flex items-center gap-3 mb-2">
          <button onClick={() => setProfileSection(null)} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Informazioni</h2>
        </div>
        <div className="bg-white rounded-2xl p-6 shadow-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <span className="text-4xl">💊</span>
          </div>
          <h3 className="text-xl font-black text-gray-900">FarmaciaSubito</h3>
          <p className="text-gray-500 text-sm mt-1">Versione 1.0.0</p>
          <p className="text-gray-400 text-xs mt-1">© 2026 FarmaciaSubito — Benevento, Italia</p>
        </div>

        {/* CONSENSO MARKETING — revocabile in qualsiasi momento (GDPR) */}
        <div className="bg-white rounded-2xl shadow-md p-4 mb-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={consensoMarketing}
              onChange={async (e) => {
                const nuovo = e.target.checked;
                setConsensoMarketing(nuovo);
                try {
                  await setDoc(doc(db, 'users', currentUser.uid), {
                    consensoMarketing: nuovo,
                    consensoMarketingData: nuovo ? new Date().toISOString() : null
                  }, { merge: true });
                } catch (err) {
                  alert('Errore nel salvataggio. Riprova.');
                  setConsensoMarketing(!nuovo);
                }
              }}
              className="mt-0.5 w-5 h-5 accent-green-600 flex-shrink-0"
            />
            <span className="text-sm text-gray-700 leading-relaxed">
              <strong>Offerte e novità</strong>
              <span className="block text-xs text-gray-400 mt-1">
                Ricevi notifiche su promozioni e nuovi prodotti. Le notifiche sui tuoi ordini
                arrivano comunque, anche se disattivi questa opzione.
              </span>
            </span>
          </label>
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {[
            { label: 'Privacy Policy', url: 'https://www.farmaciasubito.it/privacy-policy.html' },
            { label: 'Termini e Condizioni', url: 'https://www.farmaciasubito.it/termini-condizioni.html' },
            { label: 'Cookie Policy', url: 'https://www.farmaciasubito.it/cookie.html' },
            { label: 'Cancellazione Dati', url: 'https://www.farmaciasubito.it/cancellazione-dati.html' },
          ].map((item, i) => (
            <a key={i} href={item.url} target="_blank" rel="noopener noreferrer"
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition border-b border-gray-100 last:border-0">
              <span className="font-semibold text-gray-800">{item.label}</span>
              <ChevronRight className="text-gray-400" size={20} />
            </a>
          ))}
        </div>
      </div>
    );

    // Home Profilo
    const initials = `${userProfile.nome?.[0] || currentUser?.email?.[0] || '?'}${userProfile.cognome?.[0] || ''}`.toUpperCase();

    return (
      <div className="space-y-6 pb-24">
        {/* Header profilo */}
        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center text-2xl font-black">
              {initials}
            </div>
            <div>
              <h2 className="text-xl font-black">
                {userProfile.nome ? `${userProfile.nome} ${userProfile.cognome}` : 'Il mio profilo'}
              </h2>
              <p className="text-green-100 text-sm">{currentUser?.email}</p>
            </div>
          </div>
        </div>

        {/* Menu voci */}
        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {[
            { icon: User, label: 'Gestisci Account', sub: 'Nome, cognome, telefono', color: 'bg-blue-100', iconColor: 'text-blue-600', action: () => setProfileSection('account') },
            { icon: Package, label: 'I miei Ordini', sub: 'Storico ordini', color: 'bg-purple-100', iconColor: 'text-purple-600', action: () => setCurrentView('orders') },
            { icon: Heart, label: 'Preferiti', sub: `${favorites.length} prodotti salvati`, color: 'bg-red-100', iconColor: 'text-red-600', action: () => setProfileSection('favorites') },
            { icon: CreditCard, label: 'Metodo di Pagamento', sub: 'Gestisci i tuoi pagamenti', color: 'bg-green-100', iconColor: 'text-green-600', action: () => setProfileSection('payment') },
            { icon: MapPin, label: 'Indirizzi', sub: `${savedAddresses.length} indirizzi salvati`, color: 'bg-orange-100', iconColor: 'text-orange-600', action: () => setProfileSection('addresses') },
            { icon: FileText, label: 'Le mie Ricette', sub: 'Gestisci le tue ricette mediche', color: 'bg-red-100', iconColor: 'text-red-600', action: () => setProfileSection('ricette') },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <button key={i} onClick={item.action}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition border-b border-gray-100">
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={item.iconColor} size={22} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.sub}</p>
                </div>
                <ChevronRight className="text-gray-400" size={20} />
              </button>
            );
          })}
        </div>

        <div className="bg-white rounded-2xl shadow-md overflow-hidden">
          {[
            { icon: HelpCircle, label: 'Aiuto', sub: 'Supporto e assistenza ordini', color: 'bg-teal-100', iconColor: 'text-teal-600', action: () => setProfileSection('help') },
            { icon: Star, label: 'Suggerisci un Prodotto', sub: 'Non lo trovi nel catalogo?', color: 'bg-purple-100', iconColor: 'text-purple-600', action: () => setCurrentView('suggest') },
            { icon: Globe, label: 'Lingua', sub: 'Italiano', color: 'bg-indigo-100', iconColor: 'text-indigo-600', action: () => setShowLangPopup(true) },
            { icon: Bell, label: 'Preferenze di Contatto', sub: 'Email e notifiche', color: 'bg-yellow-100', iconColor: 'text-yellow-600', action: () => setProfileSection('notifications') },
            { icon: FileText, label: 'FAQ', sub: 'Domande frequenti', color: 'bg-pink-100', iconColor: 'text-pink-600', action: () => setProfileSection('faq') },
            { icon: Info, label: 'Informazioni', sub: 'Versione app, privacy, termini', color: 'bg-gray-100', iconColor: 'text-gray-600', action: () => setProfileSection('info') },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <button key={i} onClick={item.action}
                className="w-full flex items-center gap-4 p-4 hover:bg-gray-50 transition border-b border-gray-100 last:border-0">
                <div className={`w-12 h-12 ${item.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                  <Icon className={item.iconColor} size={22} />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-gray-900">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.sub}</p>
                </div>
                <ChevronRight className="text-gray-400" size={20} />
              </button>
            );
          })}
        </div>

        <button onClick={handleLogout}
          className="w-full bg-red-50 border-2 border-red-200 text-red-600 py-4 rounded-2xl font-bold hover:bg-red-100 transition flex items-center justify-center gap-2">
          <LogOut size={20} />
          Esci dall'Account
        </button>
      </div>
    );
  };

  // SUGGEST VIEW
  const SuggestView = () => {
    const [nome, setNome] = React.useState('');
    const [farmacia, setFarmacia] = React.useState('');
    const [note, setNote] = React.useState('');
    const [foto, setFoto] = React.useState(null);
    const [fotoPreview, setFotoPreview] = React.useState(null);
    const [sending, setSending] = React.useState(false);
    const [sent, setSent] = React.useState(false);

    const handleFoto = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      setFoto(file);
      const reader = new FileReader();
      reader.onloadend = () => setFotoPreview(reader.result);
      reader.readAsDataURL(file);
    };

    const handleSubmit = async () => {
      if (!nome.trim()) { alert('Inserisci il nome del prodotto'); return; }
      setSending(true);
      try {
        let fotoUrl = '';
        if (foto) {
          // Carica foto su Firebase Storage
          const { getStorage, ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
          const storage = getStorage();
          const storageRef = ref(storage, `suggerimenti/${currentUser.uid}_${Date.now()}_${foto.name}`);
          await uploadBytes(storageRef, foto);
          fotoUrl = await getDownloadURL(storageRef);
        }
        await addDoc(collection(db, 'suggerimenti-prodotti'), {
          nomeProdotto: nome.trim(),
          farmacia: farmacia.trim(),
          note: note.trim(),
          fotoUrl,
          userId: currentUser?.uid || '',
          userEmail: currentUser?.email || '',
          status: 'nuovo',
          createdAt: serverTimestamp()
        });
        setSent(true);
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setSending(false);
      }
    };

    if (sent) return (
      <div className="space-y-6 pb-24">
        <div className="flex items-center gap-3">
          <button onClick={() => setCurrentView('home')} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Suggerimento Inviato</h2>
        </div>
        <div className="bg-white rounded-3xl p-8 shadow-md text-center">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-2xl font-black text-gray-900 mb-2">Grazie!</h3>
          <p className="text-gray-500 mb-6">Il tuo suggerimento è stato inviato al team FarmaciaSubito. Lo valuteremo e lo aggiungeremo al catalogo!</p>
          <button onClick={() => setCurrentView('home')}
            className="w-full bg-green-600 text-white py-4 rounded-2xl font-black text-lg">
            Torna alla Home
          </button>
        </div>
      </div>
    );

    return (
      <div className="space-y-6 pb-24">
        <div className="flex items-center gap-3">
          <button onClick={() => setCurrentView('home')} className="p-2 hover:bg-gray-100 rounded-xl">
            <ChevronRight className="rotate-180 text-gray-600" size={24} />
          </button>
          <h2 className="text-xl font-black text-gray-900">Suggerisci un Prodotto</h2>
        </div>

        {/* Hero */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-3xl p-6 text-white text-center">
          <div className="text-5xl mb-3">💊</div>
          <h3 className="text-xl font-black mb-2">Non trovi quello che cerchi?</h3>
          <p className="text-purple-100 text-sm">Dicci quale prodotto vuoi e dove lo trovi. Lo aggiungeremo al catalogo per tutti!</p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-3xl p-6 shadow-md space-y-5">

          {/* Nome prodotto */}
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">💊 Nome del Prodotto *</label>
            <input
              type="text"
              value={nome}
              onChange={e => setNome(e.target.value)}
              placeholder="Es. Voltaren 1% gel 100g"
              className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-purple-500 outline-none text-gray-800"
            />
          </div>

          {/* Farmacia */}
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">🏪 Dove lo trovi? (opzionale)</label>
            <input
              type="text"
              value={farmacia}
              onChange={e => setFarmacia(e.target.value)}
              placeholder="Es. Farmacia Centrale, Via Roma 5"
              className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-purple-500 outline-none text-gray-800"
            />
            <p className="text-xs text-gray-400 mt-1">Indica il nome o l'indirizzo della farmacia dove lo acquisti solitamente</p>
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">📝 Note aggiuntive (opzionale)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="Es. formato specifico, dosaggio, marca alternativa..."
              rows={3}
              className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-purple-500 outline-none text-gray-800 resize-none"
            />
          </div>

          {/* Foto */}
          <div>
            <label className="block text-sm font-black text-gray-800 mb-2">📸 Foto del Prodotto (opzionale)</label>
            {fotoPreview ? (
              <div className="relative">
                <img src={fotoPreview} alt="Preview" className="w-full h-48 object-cover rounded-2xl" />
                <button
                  onClick={() => { setFoto(null); setFotoPreview(null); }}
                  className="absolute top-2 right-2 w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-36 border-2 border-dashed border-purple-300 rounded-2xl cursor-pointer hover:bg-purple-50 transition bg-gray-50">
                <div className="text-4xl mb-2">📷</div>
                <p className="text-sm font-bold text-purple-600">Tocca per aggiungere una foto</p>
                <p className="text-xs text-gray-400">JPG, PNG fino a 5MB</p>
                <input type="file" accept="image/*" onChange={handleFoto} className="hidden" />
              </label>
            )}
          </div>

          {/* Submit */}
          <button
            onClick={handleSubmit}
            disabled={sending || !nome.trim()}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-4 rounded-2xl font-black text-lg shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {sending ? (
              <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Invio in corso...</>
            ) : (
              '💡 Invia Suggerimento'
            )}
          </button>
        </div>
      </div>
    );
  };

  // SEGNALA PROBLEMA MODAL
  const ProblemaModal = () => {
    const [tipo, setTipo] = React.useState('');
    const [dettagli, setDettagli] = React.useState('');
    const [sending, setSending] = React.useState(false);

    if (!showProblemaModal) return null;

    const problemi = [
      { id: 'prodotto_mancante', label: '📦 Prodotto mancante', desc: 'Non ho ricevuto tutti i prodotti ordinati' },
      { id: 'prodotto_sbagliato', label: '❌ Prodotto sbagliato', desc: 'Ho ricevuto un prodotto diverso da quello ordinato' },
      { id: 'prodotto_danneggiato', label: '💔 Prodotto danneggiato', desc: 'Il prodotto è arrivato rotto o danneggiato' },
      { id: 'rider_scortese', label: '😠 Rider scortese', desc: 'Il rider è stato maleducato o poco professionale' },
      { id: 'consegna_ritardata', label: '⏰ Consegna in ritardo', desc: 'La consegna ha impiegato molto più del previsto' },
      { id: 'altro', label: '💬 Altro problema', desc: 'Descrivimi il problema' },
    ];

    const handleSubmit = async () => {
      if (!tipo) { alert('Seleziona il tipo di problema'); return; }
      setSending(true);
      try {
        await addDoc(collection(db, 'segnalazioni'), {
          orderId: currentOrder.id,
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          tipo,
          dettagli: dettagli.trim(),
          status: 'aperta',
          createdAt: serverTimestamp()
        });
        localStorage.setItem(`problema_${currentOrder.id}`, 'true');
        setShowProblemaModal(false);
        alert('✅ Segnalazione inviata! Ti contatteremo entro 24 ore.');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50 p-4">
        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl mb-2">
          <div className="bg-gradient-to-r from-red-500 to-orange-500 p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black">⚠️ Segnala Problema</h2>
                <p className="text-red-100 text-sm">Ordine #{currentOrder?.id?.substring(0, 8)}</p>
              </div>
              <button onClick={() => setShowProblemaModal(false)} className="p-2 hover:bg-white hover:bg-opacity-20 rounded-xl">
                <X className="text-white" size={20} />
              </button>
            </div>
          </div>
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            <p className="text-sm font-bold text-gray-700">Che tipo di problema hai riscontrato?</p>
            <div className="space-y-2">
              {problemi.map(p => (
                <button key={p.id} onClick={() => setTipo(p.id)}
                  className={`w-full text-left p-3 rounded-xl border-2 transition ${
                    tipo === p.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                  <p className="font-bold text-gray-900 text-sm">{p.label}</p>
                  <p className="text-xs text-gray-500">{p.desc}</p>
                </button>
              ))}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Dettagli aggiuntivi (opzionale)</label>
              <textarea
                value={dettagli}
                onChange={e => setDettagli(e.target.value)}
                placeholder="Descrivi il problema in dettaglio..."
                rows={3}
                className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-red-400 outline-none text-sm resize-none"
              />
            </div>
            <div className="bg-blue-50 p-3 rounded-xl text-xs text-blue-700">
              📞 Ti contatteremo via email entro 24 ore per risolvere il problema.
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowProblemaModal(false)} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-xl font-bold">Annulla</button>
              <button onClick={handleSubmit} disabled={sending || !tipo}
                className="flex-1 bg-red-600 text-white py-3 rounded-xl font-bold hover:bg-red-700 transition disabled:bg-gray-400">
                {sending ? 'Invio...' : '📨 Invia Segnalazione'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // REVIEW MODAL
  const ReviewModal = () => {
    const [stelle, setStelle] = React.useState(0);
    const [commento, setCommento] = React.useState('');
    const [hover, setHover] = React.useState(0);
    const [sending, setSending] = React.useState(false);

    if (!showReviewModal || !currentOrder?.riderId) return null;

    const handleSubmit = async () => {
      if (stelle === 0) { alert('Seleziona almeno una stella!'); return; }
      setSending(true);
      try {
        await addDoc(collection(db, 'riders', currentOrder.riderId, 'recensioni'), {
          orderId: currentOrder.id,
          stelle,
          commento: commento.trim(),
          userId: currentUser?.uid,
          userEmail: currentUser?.email,
          createdAt: serverTimestamp()
        });
        // La media stelle del rider viene ricalcolata lato server dalla Cloud
        // Function aggiornaMediaRecensioni (il cliente non può modificare il doc rider).
        localStorage.setItem(`reviewed_${currentOrder.id}`, 'true');
        setShowReviewModal(false);
        alert('✅ Grazie per la tua recensione!');
      } catch (e) {
        alert('❌ Errore: ' + e.message);
      } finally {
        setSending(false);
      }
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50 p-4">
        <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl mb-2">
          <div className="bg-gradient-to-r from-yellow-400 to-orange-400 p-6 text-center">
            <div className="text-5xl mb-2">⭐</div>
            <h2 className="text-2xl font-black text-white">Valuta il tuo Rider!</h2>
            <p className="text-yellow-100 text-sm mt-1">Come è andata la consegna?</p>
          </div>
          <div className="p-6 space-y-5">
            {/* Nome rider */}
            {currentOrder?.riderName && (
              <p className="text-center text-gray-600 font-semibold">
                🛵 Rider: <strong>{currentOrder.riderName}</strong>
              </p>
            )}
            {/* Stelle */}
            <div className="flex justify-center gap-3">
              {[1, 2, 3, 4, 5].map(s => (
                <button key={s}
                  onClick={() => setStelle(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                  className="text-4xl transition-transform hover:scale-125"
                >
                  {s <= (hover || stelle) ? '⭐' : '☆'}
                </button>
              ))}
            </div>
            {stelle > 0 && (
              <p className="text-center text-sm font-bold text-yellow-600">
                {stelle === 1 ? 'Pessimo 😞' : stelle === 2 ? 'Scarso 😕' : stelle === 3 ? 'Nella norma 😐' : stelle === 4 ? 'Buono 😊' : 'Eccellente! 🤩'}
              </p>
            )}
            {/* Commento */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Commento (opzionale)</label>
              <textarea
                value={commento}
                onChange={e => setCommento(e.target.value)}
                placeholder="Racconta la tua esperienza..."
                rows={3}
                className="w-full px-4 py-3 rounded-2xl border-2 border-gray-200 focus:border-yellow-400 outline-none text-sm resize-none"
              />
            </div>
            {/* Bottoni */}
            <div className="flex gap-3">
              <button onClick={() => { setShowReviewModal(false); localStorage.setItem(`reviewed_${currentOrder.id}`, 'true'); }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-2xl font-bold">
                Salta
              </button>
              <button onClick={handleSubmit} disabled={sending || stelle === 0}
                className="flex-1 bg-yellow-400 text-yellow-900 py-3 rounded-2xl font-bold hover:bg-yellow-500 transition disabled:opacity-50">
                {sending ? 'Invio...' : '⭐ Invia Recensione'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // FAQ Item con accordion
  const FaqItem = ({ question, answer }) => {
    const [open, setOpen] = React.useState(false);
    return (
      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-4 text-left">
          <span className="font-bold text-gray-900 pr-4">{question}</span>
          <ChevronRight className={`text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} size={20} />
        </button>
        {open && (
          <div className="px-4 pb-4 text-sm text-gray-600 border-t border-gray-100 pt-3">
            {answer}
          </div>
        )}
      </div>
    );
  };

  // SETUP CARD FORM
    const SetupCardForm = ({ onSuccess, onCancel, clientSecret }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = React.useState(null);
    const [processing, setProcessing] = React.useState(false);

    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!stripe || !elements) return;
      setProcessing(true);
      setError(null);
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
          clientSecret,
          { payment_method: { card: cardElement } }
        );
      if (stripeError) {
        setError(stripeError.message);
        setProcessing(false);
        return;
      }
      if (setupIntent.status === 'succeeded') {
        alert('✅ Carta salvata con successo!');
        onSuccess();
      }
      setProcessing(false);
    };

    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">Dati Carta</label>
          <div className="border-2 border-gray-200 rounded-xl p-4 bg-white focus-within:border-green-500">
            <CardElement options={{
              style: {
                base: { color: '#1f2937', fontFamily: 'Arial, sans-serif', fontSize: '16px', '::placeholder': { color: '#9ca3af' } },
                invalid: { color: '#ef4444' }
              }
            }} />
          </div>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">{error}</div>}
        <div className="flex gap-3">
          <button type="button" onClick={onCancel} className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-xl font-bold">Annulla</button>
          <button type="submit" disabled={!stripe || processing}
            className="flex-1 bg-green-600 text-white py-3 rounded-xl font-bold hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center gap-2">
            {processing ? <><div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>Salvataggio...</> : '💳 Salva Carta'}
          </button>
        </div>
        <p className="text-xs text-gray-400 text-center">🔒 Pagamento sicuro tramite Stripe</p>
      </form>
    );
  };

  const BottomNav = () => (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-40">
      <div className="flex justify-around items-center max-w-md mx-auto">
        {[
          { id: 'home', icon: Home, label: 'Home' },
          { id: 'products', icon: Search, label: 'Prodotti' },
          { id: 'cart', icon: ShoppingCart, label: 'Carrello', badge: cart.length },
          { id: 'orders', icon: Package, label: 'Ordini' },
          { id: 'profile', icon: User, label: 'Profilo' }
        ].map(item => {
          const Icon = item.icon;
          return (
            <button
              key={item.id}
              onClick={() => setCurrentView(item.id)}
              className={`relative flex flex-col items-center gap-1 ${
                currentView === item.id ? 'text-green-600' : 'text-gray-400'
              }`}
            >
              <Icon size={24} />
              {item.badge > 0 && (
                <span className="absolute -top-1 -right-2 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
              <span className="text-xs font-semibold">{item.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const Notification = () => {
    if (!showNotification) return null;
    
    return (
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-6 py-3 rounded-full shadow-lg z-50 flex items-center gap-2">
        <CheckCircle size={20} />
        <span className="font-semibold">Prodotto aggiunto al carrello!</span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 p-4 sticky top-0 z-30">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Logo />
          <button
            onClick={handleLogout}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <LogOut className="text-gray-600" size={20} />
          </button>
        </div>
      </div>

      <div className="max-w-md mx-auto p-4">
        {/* Servizio sospeso: home, catalogo e carrello sono bloccati (niente nuovi
            ordini). Ordini, tracking, chat e profilo restano accessibili per chi ha
            un ordine già in corso. */}
        {!servizioAttivo && ['home', 'products', 'cart', 'suggest'].includes(currentView) ? (
          <ServizioSospeso onVediOrdini={() => setCurrentView('orders')} />
        ) : (
          <>
            {currentView === 'home' && <HomeView />}
            {currentView === 'products' && <ProductsView />}
            {currentView === 'cart' && <CartView />}
            {currentView === 'tracking' && <TrackingView />}
            {currentView === 'orders' && <OrdersView />}
            {currentView === 'chat' && <ChatView />}
            {currentView === 'profile' && <ProfileView />}
            {currentView === 'suggest' && <SuggestView />}
          </>
        )}
      </div>

      {currentView !== 'chat' && <BottomNav />}
      <Notification />
      
      <ReviewModal />
      <ProblemaModal />
     
      {/* BANNER SUGGERISCI PRODOTTO */}
      {showSuggestBanner && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-end justify-center z-50 p-4">
          <div className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl mb-2">
            <div className="bg-gradient-to-r from-purple-600 to-pink-500 p-6 text-white text-center relative">
              <button
                onClick={() => { setShowSuggestBanner(false); localStorage.setItem('suggestBannerSeen', new Date().toDateString()); }}
                className="absolute top-4 right-4 w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center"
              >
                <X size={16} className="text-white" />
              </button>
              <div className="text-5xl mb-3">💊</div>
              <h2 className="text-2xl font-black mb-1">Non trovi quello che cerchi?</h2>
              <p className="text-purple-100 text-sm">Suggeriscici il prodotto! Lo aggiungeremo al catalogo.</p>
            </div>
            <div className="p-6 space-y-3">
              <p className="text-gray-600 text-sm text-center">
                Conosci un farmaco o prodotto che vorresti ordinare? Dimmelo e lo troverai presto nel catalogo!
              </p>
              <button
                onClick={() => {
                  setShowSuggestBanner(false);
                  localStorage.setItem('suggestBannerSeen', new Date().toDateString());
                  setCurrentView('suggest');
                }}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white py-4 rounded-2xl font-black text-lg shadow-lg"
              >
                💡 Suggerisci un Prodotto
              </button>
              <button
                onClick={() => { setShowSuggestBanner(false); localStorage.setItem('suggestBannerSeen', new Date().toDateString()); }}
                className="w-full text-gray-400 text-sm py-2"
              >
                Non ora
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmaciaSubito;